import { PartnerPortfolio, type PartnerPortfolioHandle } from "../produktpartner/PartnerPortfolio";
// ContactDetailsRedesign.tsx — Kontakt-Detailansicht im „Refined Classic“-Redesign.
//
// Allgemein-Tab voll an die API angebunden (laden/editieren/speichern), Firma &
// Person. Iteration 2: fixierter (sticky) Header mit Prev/Next-Pfeilen statt
// Action-Buttons, RBAC-gated Bottom-Speicherleiste (canWrite vom Backend),
// Pflichtfeld-Validierung (Firmenname/Nachname), Gold-Divider, Erreichbarkeit als
// Sektion-Button, farbige Social-Icons (SocialChannel), Firma-vs-Person-
// Kommunikation, und der Verknüpfungen-Block (echte Daten, read-only) am Ende.

import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router";
import { findScrollParent } from "../../hooks/useFillViewportHeight";
import { useKopfKompaktBeimScrollen } from "./useKopfKompaktBeimScrollen";
import { useBerufsstatusOptions, withBerufsstatusValue } from "../../hooks/useBerufsstatusOptions";
import { Toaster, Toast, ToastTitle, ToastBody, useToastController, mergeClasses } from "@fluentui/react-components";
import { I, FirmaIcon, PersonIcon } from "./kvpIcons";
import { TagInput, NationalitySelect, CountrySelect, FinanzamtSelect, SectionCard } from "../forms";
import { IbanEingabe } from "../forms/widgets/IbanEingabe";
import { formatIbanForDisplay, validateIban } from "../forms/validation/iban";
import type { BankTreffer } from "../forms/widgets/bankleitzahlen";
import { kontoinhaberVorschlag } from "../../utils/kontoinhaber";
import { DataTable, type Column, Dash, GhostIconButton } from "./table";
import { DataFormDialog, type DataTab } from "./DataFormDialog";
import {
  VERSTORBEN_ZEICHEN,
  displayContactName,
  istVerstorben,
  verstorbenTitel,
} from "../../utils/contactDisplay";
import { geschlechtAusAnrede } from "../../utils/anredeGeschlecht";
import ProfilbildDialog from "./ProfilbildDialog";
import { contactTypeLabel } from "../../utils/contactTypeLabel";
import { apiUrl } from "../../utils/api";
import { apiFetch } from "../../utils/apiFetch";
import { AuthedImage } from "../common/AuthedImage";
import { CollapseToggle } from "../common/CollapseToggle";
import { AkteAllgemeinAnlage, type AkteAnlageHandle } from "./AkteAllgemeinAnlage";
import { SchlossRahmen } from "./SchlossHinweis";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { KopfDokumentLeiste } from "./KopfDokumentLeiste";
import { PersoenlicherKontaktPille } from "./PersoenlicherKontaktPille";
import { geschlechtKurz } from "../relationsInline/beziehungsAnzeige";
import { usePersistentCollapsed } from "../../hooks/usePersistentCollapsed";
import {
  AKTE_KARTE_KEY_PREFIX,
  AkteAnsichtCtx,
  andereAnsicht,
  gemerkteAnsicht,
  merkeAnsicht,
  umschalterTitel,
  useAkteAnsicht,
  type AkteAnsicht,
} from "./akteAnsicht";
import {
  REIHENFOLGE,
  type AbschnittsSchluessel,
  type AkteVariante,
} from "./akteAbschnitte";
import { WeitereEintraege, WeitereZeile } from "./WeitereZeile";
import { weitereAnschriften, weitereBankverbindungen, weitereText } from "./weitereEintraege";
import { SocialChannel } from "./SocialChannel";
import { AvailabilityButton } from "./AvailabilityButton";
import { ContactConnections, ANCHOR_HAUSHALT, ANCHOR_VERBINDUNGEN } from "./ContactConnections";
import { ContactStatusPill, type StatusJumpTarget } from "./ContactStatusPill";
import { VerfuegungenListe, VertretungenListe } from "./VertretungUndVollmacht";
import { verfuegungenAusApi, zeigeVerfuegungen, type VerfuegungForm } from "./vertretungLogik";
import {
  ZuwendungenListe,
  zuwendungenAusApi,
  type ZuwendungItem,
} from "../relationsInline/ZuwendungenListe";
import { EINLADUNGS_KENNZEICHEN } from "../../data/zuwendungsarten";
import {
  AUSWEIS_ARTEN,
  UNTERARTEN,
  dokumentFelder,
  klassenLesen,
  klasseUmschalten,
} from "../../data/ausweisarten";
import { MehrfachSelect } from "../forms/widgets/MehrfachSelect";
import { anschriftBezeichnung } from "../../data/adresstypen";
import { SELBSTSTAENDIGKEIT_OPTIONEN } from "../../data/selbststaendigkeit";
import { VerantwortlichChip } from "./VerantwortlichChip";
import { FoMehrfachChip } from "./FoMehrfachChip";
import { foListeAusKontakt } from "../../utils/foUserIds";
import { verantwortungVon } from "../../utils/bearbeitenBeziehungen";
import { VollmachtenAkteInhalt } from "../vollmachten/VollmachtenKarte";
import { ContactOrgchartChip } from "./ContactOrgchartChip";
import { ContactTodesfallBelege } from "./ContactTodesfallBelege";
import { todesfallBelegeAusApi } from "../../data/todesfallBelege";
import {
  APPROVAL_INITIAL,
  approvalGesendet,
  approvalMeldung,
  type ApprovalAnfrage,
  type ApprovalRequestResponse,
  type ApprovalUiState,
} from "./ApprovalRequestDialog";
import { KeineRechteDialog, type SchreibSperre } from "./KeineRechteDialog";
import { useFreigabeEntscheidungLive } from "./useFreigabeEntscheidungLive";
import { BrancheZweiSelect } from "../forms/widgets/BrancheZweiSelect";
import { OptionChips } from "../forms/widgets/OptionChips";
import { UNTERNEHMENSFUNKTIONEN } from "../../data/unternehmensfunktion";
import AnalyseButton from "../contactRisks/AnalyseButton";
import { useBranchengruppen } from "./useBranchengruppen";
import type { BrancheGruppenWahl } from "../forms/widgets/brancheGruppenLogik";
// Nur für die BESTANDSANZEIGE: Firmen, die vor dem 08.09.2026 eingeordnet
// wurden, tragen eine `brancheClassId` aus dem dreistufigen Altkatalog. Sie
// wird hier ausschließlich gelesen und angezeigt — nie geschrieben.
import { formatBranchePath, findLeaf, useBrancheClassification } from "../../data/brancheClassification";
import { HandelsregisterAbdruck } from "./HandelsregisterAbdruck";
import { derivePersonEmployment } from "../../utils/derivePersonEmployment";
import {
  arbeitgeberErmitteln,
  arbeitgeberKandidaten,
  type ArbeitgeberBefund,
} from "../../utils/arbeitgeber";
import { arbeitgeberSetzen } from "../../services/arbeitgeberService";
import { ArbeitgeberFeld } from "./ArbeitgeberFeld";
import { MenschenFuersUnternehmenDialog } from "./MenschenFuersUnternehmenDialog";
import {
  STEUERKLASSEN,
  datumAusEingabe,
  datumFuersFeld,
} from "../../utils/steuerUndAusweisAngaben";
import { SperrWert } from "../forms/SperrWert";
import { GroupedNumberInput } from "../common/GroupedNumberInput";
import { useContactGeneralForm } from "./useContactGeneralForm";
import { KommunikationSpalten, KommFeldBeschriftung } from "./KommunikationSpalten";
import {
  KOMM_FELDER_PERSON,
  firmaFuerGeschaeftlichesFeld,
  firmenDerZeile,
  kommArt,
  type KommFeldPlan,
} from "./kommunikationSpaltenPlan";
import { firmenAnzeige, firmenVoll } from "../../utils/firmenAnzeige";
import { uebernahmeSchritte, vorhandeneAkteWerte } from "./firmendatenAkteFelder";
import { ortsteilLeeren } from "../../utils/ortsteilAbgleich";
import FirmendatenBefuellenDialog, { type WeitereWerte } from "../FirmendatenBefuellenDialog";
import type { BefundFelder } from "../../services/firmendatenService";
import { useContactPresence } from "./useContactPresence";
import { ContactPresenceBar } from "./ContactPresenceBar";
import { FeldKopf, FeldPraesenzHuelle } from "./FeldPraesenz";
import { useTelephony } from "../../context/TelephonyContext";
import type { ContactDetailsFormValues } from "../../types/Contact";
import { AMTSGERICHTE, formatHandelsregister } from "../../data/amtsgerichte";
import { GeschaeftsjahrField } from "./GeschaeftsjahrField";
import { AufzaehlungsLeiste, useAufzaehlung } from "../forms/widgets/useAufzaehlung";
import { AoZweckeFeld } from "./AoZweckeFeld";
import { rechtsformOptionAus, unternehmensBegriffAus } from "../../utils/unternehmensBegriff";
import {
  fuehrtGemeinnuetzigkeit,
  koerperschaftTexte,
  zeigeFreistellungsbescheid,
  zeigeRegisterFeld,
} from "../../utils/koerperschaftBegriff";
import { normalizeWebUrl, normalizeWebUrlForStorage, WEB_URL_MAX_LENGTH } from "../../utils/webUrl";
import {
  HERKUNFT_OPTIONEN,
  HERKUNFT_SONSTIGES,
  herkunftZerlegen,
} from "../../utils/kontaktherkunft";
import {
  FAMILIENSTAND_OPTIONS,
  FAMILIENSTAND_SONSTIGES,
  FAMILIENSTAND_SONSTIGES_PLATZHALTER,
  familienstandZerlegen,
  familienstandZusammensetzen,
  zeigeHochzeitstag,
} from "../../utils/familienstand";
import KennzahlenTab from "../KennzahlenTab";
import ContactRisksPanel from "../contactRisks/ContactRisksPanel";
import Vertragsaufstellung from "../Vertragsaufstellung";
import DamagesTableContact from "../DamagesTableContact";
import { KontaktVorgaengeReiter } from "../kontaktVorgaenge/KontaktVorgaengeReiter";
import { ContactKommunikationView } from "../ContactKommunikationView";
import { ContactTermineTab } from "../contactTermine/ContactTermineTab";
import { ContactKundentagebuchTab } from "../kundentagebuch/ContactKundentagebuchTab";
import { DocumentsSection } from "../DocumentsSection";
import { ordnerAusParam } from "../mail/schadenOrdner";
import OffenePostenTabelle from "./OffenePostenTabelle";
import { useAuth } from "../../context/AuthContext";
import { visibleTabs, isTabVisible, resolveTab, isPartnerStatus } from "./contactTabs";
import { PartnerAnsprechpartnerListe } from "../produktpartner/PartnerAnsprechpartnerListe";
import { PartnerProfilSektion } from "../produktpartner/PartnerProfilSektion";
import "./contactRedesign.css";
import { TelefonEingabe } from "../forms/widgets/TelefonEingabe";
import { usePersonZentrale } from "../forms/widgets/firmenZentrale";
import { formatiereTelefon } from "@kmvs/forms-core";
import { useUngespeichertFrage } from "./UngespeichertDialog";
import { useVerlassenSperre } from "../../hooks/useVerlassenSperre";
import { bisherigeStatus } from "../../utils/kontaktKonflikt";
import { eigenenStandMelden } from "../../utils/kontaktStand";
import { hinweisen } from "./bestaetigen";
import { RelationCreateDialog } from "./RelationCreateDialog";
import { genderOf } from "../../utils/householdRole";
import { hatLebendenPartner, partnerErfassenFragen, partnerFrageFuerStatus, partnerImHaushalt } from "../relationsInline/partnerErfassen";

// Hinweis (SP4/Entscheidung 75): Der Haupt-Tab heißt schlicht „Kommunikation“
// (Mails + Anrufe); der gleichnamige Stammdaten-Modal-Tab wurde zur Abgrenzung
// in „Kontaktdaten“ umbenannt (DM_TABS, s. u.).
// Die Tab-Liste selbst und die Deep-Link-Auflösung liegen in contactTabs.ts.

// Auswahl-Konstanten (1:1 aus der alten ContactDetails portiert).
// Familienstand: Liste, Sentinel und das Zerlegen/Zusammensetzen des EINEN
// gespeicherten Wertes stehen in utils/familienstand — die Anlage-Maske liest
// und schreibt denselben Wert über dieselben Funktionen.
// Berufsstatus: siehe hooks/useBerufsstatusOptions (Katalog aus dem Backend).
const JANEIN = ["Ja", "Nein"];
const SOCIAL_PLATFORMS = ["linkedin", "instagram", "facebook", "tiktok", "xing", "youtube", "homepage"] as const;
// Die Website wird als eigenes Feld in der Kommunikation gepflegt (mit Knopf zum
// Öffnen) — als Social-Icon daneben gäbe es zwei Eingabewege für denselben Wert,
// die sich bei mehreren homepage-Zeilen widersprechen. In den Ausschluss-Filtern
// (Kontaktdaten-Modal, weitere Kommunikationswege) bleibt `homepage` dagegen
// stehen, sonst stünde die Website dort ein zweites Mal, roh beschriftet.
const SOCIAL_ROW_PLATFORMS = SOCIAL_PLATFORMS.filter((p) => p !== "homepage");

/** Anzeigename der Kommunikationsart — für die Liste der weiteren Wege. */
const TYP_LABEL: Record<string, string> = {
  phone: "Telefon",
  mobile: "Mobil",
  fax: "Telefax",
  email: "E-Mail",
};

const STACK_ICON = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round">
    <rect x="7.5" y="2.5" width="10" height="10" rx="2.2" />
    <path d="M12.5 15.5A1.5 1.5 0 0111 17h-6A1.5 1.5 0 013.5 15.5v-6A1.5 1.5 0 015 8" strokeLinecap="round" />
  </svg>
);
const CHEV_R = (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3.5L10.5 8 6 12.5" />
  </svg>
);
/** localStorage-Schlüssel des zugeklappten Kontaktkopfs. */
const KOPF_ZU_KEY = "kvp-rd.kontaktkopf-zu";

/** Umschalter im Kopf: gestapelte Karten (Ziel Kartenansicht) bzw. Zeilenliste
 *  (Ziel klassische Ansicht). Inline-SVG wie die übrigen Kopf-Symbole. */
const KARTEN_ICON = (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinejoin="round">
    <rect x="3" y="3.5" width="14" height="5.5" rx="1.6" />
    <rect x="3" y="11" width="14" height="5.5" rx="1.6" />
  </svg>
);
const LISTE_ICON = (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
    <path d="M3 5.5h14M3 10h14M3 14.5h14" />
  </svg>
);

const SEC_ICONS = {
  num: <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round"><path d="M7 3.5L5.5 16.5M14.5 3.5L13 16.5M3.5 7.5h13M3 12.5h13" /></svg>,
  info: <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinejoin="round"><path d="M4 17V6L10 3.5 16 6v11" /><path d="M3 17h14" strokeLinecap="round" /><path d="M7 8h0M7 11h0M7 14h0M13 8h0M13 11h0M13 14h0" strokeLinecap="round" /></svg>,
  legal: <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M10 3v14M5 6h10M6.5 6L4 11h5zM13.5 6L11 11h5z" /><path d="M3 17h14" /></svg>,
  pin: <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinejoin="round"><path d="M10 18s6-5 6-9.5A6 6 0 004 8.5C4 13 10 18 10 18z" /><circle cx="10" cy="8.5" r="2.2" /></svg>,
  comm: <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinejoin="round"><path d="M3.5 15V5.5A1.5 1.5 0 015 4h10a1.5 1.5 0 011.5 1.5v6A1.5 1.5 0 0115 13H7l-3.5 3z" /></svg>,
  branche: <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M3 16.5V9l4.5 3V9l4.5 3V5.5l4.5 2.3v8.7z" /><path d="M2.5 16.5h15" /></svg>,
  bank: <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M10 2.5l7 3.5H3z" /><path d="M4.5 6v8M8 6v8M12 6v8M15.5 6v8M3 16.5h14" /></svg>,
};

// ---- field helpers (controlled; read-only wenn ohne onChange) ----------------
type SelOption = string | { value: string; label: string };
type FldProps = {
  label: string;
  value?: string;
  onChange?: (v: string) => void;
  ph?: string;
  span?: boolean;
  /** Live-Präsenz: Betreten/Verlassen des Feldes melden (useContactPresence). */
  onFocus?: () => void;
  onBlur?: () => void;
  /**
   * Anzeigename des Kollegen, der gerade in diesem Feld arbeitet. Ein HINWEIS,
   * keine Sperre — aber seit 08/2026 einer, der nur noch für DIESES Feld gilt:
   * Gespeichert werden ausschließlich die geänderten Felder
   * (kontaktSpeicherPayload), zwei Anwender an verschiedenen Feldern
   * überschreiben sich also nicht mehr. Wer dasselbe Feld pflegt, schreibt
   * weiterhin zuletzt gewinnend — genau davor warnt die Markierung.
   */
  lockedBy?: string;
};

function Text({ label, value, onChange, ph, span, type, error, maxLength, onFocus, onBlur, lockedBy }: FldProps & { type?: string; error?: string;
  /** Zeichengrenze der Spalte — ohne sie liefe die Eingabe erst beim Speichern in einen Serverfehler. */
  maxLength?: number }) {
  return (
    <div className={"fld" + (span ? " span2" : "")}>
      <FeldKopf label={label} lockedBy={lockedBy} />
      <input
        className={"inp" + (value ? "" : " is-empty") + (error ? " invalid" : "")}
        type={type}
        value={value ?? ""}
        placeholder={ph}
        maxLength={maxLength}
        readOnly={!onChange}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <SperrWert />
      {error && <span className="fld-err">{error}</span>}
    </div>
  );
}

/**
 * IBAN im selben Markup wie `Text`, aber mit dem Verhalten, das eine IBAN
 * braucht: 4er-Gruppen schon beim Tippen, Prüfziffer-Hinweis und die Meldung
 * der erkannten Bank nach oben. Bewusst eine eigene Komponente statt weiterer
 * Haken an `Text` — `Text` wird in dieser Datei dutzendfach benutzt, und eine
 * Signaturänderung dort strahlt entsprechend weit aus.
 */
function IbanFeld({ label, value, onChange, ph, onFocus, onBlur, lockedBy, onBankErkannt }: FldProps & {
  onBankErkannt?: (bank: BankTreffer) => void;
}) {
  const pruefung = validateIban(String(value ?? ""));
  const fehler = value && !pruefung.valid ? pruefung.error : undefined;
  return (
    <div className="fld">
      <FeldKopf label={label} lockedBy={lockedBy} />
      <IbanEingabe
        className={"inp" + (value ? "" : " is-empty") + (fehler ? " invalid" : "")}
        value={value ?? ""}
        placeholder={ph}
        readOnly={!onChange}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onBankErkannt={onBankErkannt}
        aria-invalid={!!fehler}
      />
      <SperrWert />
      {/* Ein Hinweis, keine Sperre: Bestandsdaten stammen aus Import und
          Zusammenführung und sind nicht durchgängig geprüft — ein Block würde
          das Speichern der ganzen Maske an einer alten Zeile scheitern lassen. */}
      {fehler && <span className="fld-err">{fehler}</span>}
    </div>
  );
}

/**
 * Mehrzeiliges Feld — für Angaben, die in eine Zeile nicht passen (Bemerkung).
 * Wächst mit dem Inhalt und bleibt vom Anwender in der Höhe ziehbar.
 */
function TextArea({ label, value, onChange, ph, span, rows = 4, onFocus, onBlur, lockedBy, aufzaehlung }: FldProps & {
  rows?: number;
  /**
   * Gliederung anbieten: Knopfleiste über dem Feld, Enter setzt den nächsten
   * Punkt fort, Tab rückt ein. Gespeichert wird weiterhin reiner Text.
   */
  aufzaehlung?: boolean;
}) {
  // Der Hook läuft immer (Regeln der Hooks); ohne `aufzaehlung` bleibt er ungenutzt.
  const auf = useAufzaehlung(value ?? "", onChange);
  return (
    <div className={"fld" + (span ? " span2" : "")}>
      <FeldKopf label={label} lockedBy={lockedBy} />
      {aufzaehlung && auf.bedienbar && <AufzaehlungsLeiste aktionen={auf.aktionen} />}
      <textarea
        ref={aufzaehlung ? auf.ref : undefined}
        className={"inp" + (value ? "" : " is-empty")}
        style={{ minHeight: 92, resize: "vertical", lineHeight: 1.45, fontFamily: "inherit" }}
        rows={rows}
        value={value ?? ""}
        placeholder={ph}
        readOnly={!onChange}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onKeyDown={aufzaehlung ? auf.onKeyDown : undefined}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <SperrWert mehrzeilig />
    </div>
  );
}

const ACT_LABEL = { phone: "Anrufen", mail: "E-Mail schreiben", web: "Website öffnen" } as const;

function ActField({ label, value, onChange, ph, action, span, maxLength, onFocus, onBlur, lockedBy, firma, firmaTitel, geschaeftlich, zentrale }: FldProps & { action: "phone" | "mail" | "web"; maxLength?: number;
  /**
   * Unternehmen, denen dieser Kommunikationsweg gehört — dezent in der
   * Label-Zeile, ggf. gekürzt (Aufbau und Begründung: {@link KommFeldBeschriftung}).
   */
  firma?: string;
  /** Voller Text der Unternehmen für den Tooltip. */
  firmaTitel?: string;
  /** Telefon: geschäftliche Nummer (Firma, Spalte „geschäftlich") — Durchwahl „-" erlaubt. */
  geschaeftlich?: boolean;
  /** Telefon: Zentralnummer(n) der Firma für die automatische Durchwahl. */
  zentrale?: string | readonly string[] }) {
  const { callNumber } = useTelephony();
  const ico = action === "mail" ? I.mail : action === "web" ? I.link : I.phone;
  const onAct = () => {
    if (!value) return;
    // Telefon: app-weiter callNumber()-Helper (SDK-Widget, ADR 0002); E-Mail bleibt mailto:.
    // Website: NUR dieser Knopf öffnet einen neuen Tab — das Feld selbst bleibt Eingabe.
    if (action === "mail") window.location.href = `mailto:${value}`;
    else if (action === "web") window.open(normalizeWebUrl(value), "_blank", "noopener,noreferrer");
    else callNumber(value);
  };
  // Beim Verlassen des Website-Feldes das Schema ergänzen: „www.kunde.de" ist
  // sonst eine relative Adresse und landet in der App statt beim Kunden.
  // Das ergänzte „https://" darf die Feldgrenze aber nicht reißen — sonst
  // scheitert der PUT an varchar(100); in dem Fall bleibt die Eingabe stehen.
  const normalisiereWeb = action === "web" && onChange
    ? () => { const url = normalizeWebUrlForStorage(value, maxLength); if (url !== (value ?? "")) onChange(url); }
    : undefined;
  const handleBlur = normalisiereWeb || onBlur
    ? () => { normalisiereWeb?.(); onBlur?.(); }
    : undefined;
  return (
    <div className={"fld" + (span ? " span2" : "")}>
      <FeldKopf label={<KommFeldBeschriftung label={label} firma={firma} firmaTitel={firmaTitel} />} lockedBy={lockedBy} />
      <div className="fld-act">
        {action === "phone" && onChange ? (
          // Telefon: einheitliches Format „+49 221 123456-78" beim Tippen.
          <TelefonEingabe
            className={"inp" + (value ? "" : " is-empty")}
            value={value ?? ""}
            placeholder={ph}
            maxLength={maxLength}
            onChange={onChange}
            geschaeftlich={geschaeftlich}
            zentrale={zentrale}
            onFocus={onFocus}
            onBlur={handleBlur}
          />
        ) : (
          <input
            className={"inp" + (value ? "" : " is-empty")}
            value={action === "phone" ? formatiereTelefon(value, { geschaeftlich: true }) : value ?? ""}
            placeholder={ph}
            maxLength={maxLength}
            readOnly={!onChange}
            onChange={onChange ? (e) => onChange(e.target.value) : undefined}
            onFocus={onFocus}
            onBlur={handleBlur}
          />
        )}
        <SperrWert />
        <button type="button" className="fld-suffix" aria-label={ACT_LABEL[action]} title={ACT_LABEL[action]} onClick={onAct}>{ico}</button>
      </div>
    </div>
  );
}

function Sel({ label, value, onChange, ph, options = [], span, blankLabel, onFocus, onBlur, lockedBy }: FldProps & { options?: SelOption[]; blankLabel?: string }) {
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  // blankLabel: die Leer-Option ist eine ECHTE Auswahl (z. B. „keine“) → schwarz
  // gerendert, kein grauer „noch nicht ausgewählt“-Placeholder-Stil.
  const isPlaceholder = !value && !blankLabel;
  return (
    <div className={"fld" + (span ? " span2" : "")}>
      <FeldKopf label={label} lockedBy={lockedBy} />
      <select
        className={"sel" + (isPlaceholder ? " placeholder" : "")}
        value={value ?? ""}
        disabled={!onChange}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <option value="">{blankLabel || ph || "— wählen —"}</option>
        {norm.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <SperrWert />
    </div>
  );
}

function DateF({ label, value, onChange, span, onFocus, onBlur, lockedBy }: FldProps) {
  return (
    <div className={"fld" + (span ? " span2" : "")}>
      <FeldKopf label={label} lockedBy={lockedBy} />
      <input
        type="date"
        className={"inp" + (value ? "" : " is-empty")}
        value={value ?? ""}
        readOnly={!onChange}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <SperrWert />
    </div>
  );
}

// Zuständiges Amtsgericht (Registergericht) — SUCHBARES Eingabefeld: tippen
// filtert die ~117 Gerichte (natives <datalist>), Auswahl per Klick/Enter.
function AmtsgerichtSel({ label, value, onChange, span, onFocus, onBlur, lockedBy }: FldProps) {
  return (
    <div className={"fld" + (span ? " span2" : "")}>
      <FeldKopf label={label} lockedBy={lockedBy} />
      <input
        className={"inp" + (value ? "" : " is-empty")}
        list="kvp-amtsgerichte"
        value={value ?? ""}
        placeholder="Amtsgericht suchen …"
        readOnly={!onChange}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <SperrWert />
      <datalist id="kvp-amtsgerichte">
        {AMTSGERICHTE.map((ort) => <option key={ort} value={ort} />)}
      </datalist>
    </div>
  );
}

type SecTool = { label: string; count?: number; icon?: React.ReactNode; muted?: boolean; title?: string; onClick?: () => void };

// Ein Abschnitt des Reiters „Allgemein“ — in ZWEI Darstellungen, gespeist aus
// denselben Kindern:
//
//   klassisch → Überschrift mit goldener Trennlinie, Klapp-Chevron LINKS
//   karten    → weiße Karte mit Schatten wie in der Kontakt-Anlage,
//               Klapp-Chevron RECHTS im Kartenkopf
//
// Dass die Umschaltung hier sitzt und nicht in den vier Layout-Funktionen, ist
// der Kern des Umbaus: keine zweite Feldliste, keine zweite Abschnittsfolge, die
// auseinanderdriften könnten — und Schreibrecht, Feld-Präsenz und die
// null-statt-Leerstring-Regeln gelten in beiden Ansichten von selbst.
function Sec({ title, icon, count, tools, toolNodes, defaultOpen = true, weitere, children }: {
  title: string; icon?: React.ReactNode; count?: number; tools?: SecTool[]; toolNodes?: React.ReactNode; defaultOpen?: boolean;
  /** Nur in der Kartenansicht: Zeile „N weitere … anzeigen“ am Fuß der Karte. */
  weitere?: React.ReactNode;
  children: React.ReactNode;
}) {
  const ansicht = useAkteAnsicht();
  // Beide Zustände IMMER anlegen, vor jeder Verzweigung: ein Ansichtswechsel
  // darf die Hook-Reihenfolge nicht verändern, sonst steht die Akte weiß da —
  // und weder type-check noch Build sehen das.
  const [open, setOpen] = useState(defaultOpen);
  // Klappzustand je Karte, gemerkt über Kontakte hinweg (wie Kopf und
  // Speicherleiste). Der Schlüssel hängt am Titel; wird ein Abschnitt umbenannt,
  // steht seine Karte einmalig wieder offen — harmlos.
  const [karteZu, karteKlappen] = usePersistentCollapsed(AKTE_KARTE_KEY_PREFIX + title, !defaultOpen);
  const hasTools = !!toolNodes || (tools != null && tools.length > 0);
  // Einmal gebaut, von beiden Zweigen benutzt — sonst driften die Werkzeuge der
  // zwei Ansichten mit der Zeit auseinander.
  const werkzeuge = hasTools ? (
    <>
      {toolNodes}
      {tools?.map((t, i) => (
        <button key={i} type="button" className={"fsec-link" + (t.muted ? " muted" : "")} title={t.title || t.label} onClick={t.onClick}>
          {t.icon}{t.label}{t.count != null && <span className="lk-badge">{t.count}</span>}
        </button>
      ))}
    </>
  ) : null;

  if (ansicht === "karten") {
    return (
      // `actions` ist in SectionCard bereits das rechte Flex-Kind — der
      // Klapp-Knopf sitzt damit ohne CSS-Eingriff rechts. Anders als in der
      // klassischen Ansicht klickt NUR der Chevron, nicht die ganze Kopfzeile:
      // daneben stehen die Werkzeug-Links, mit denen der Klick sonst kollidiert.
      <SectionCard
        title={title}
        actions={
          <span className="akte-karte-tools">
            {werkzeuge}
            <CollapseToggle collapsed={karteZu} onToggle={karteKlappen} label={title} />
          </span>
        }
      >
        {!karteZu && (
          <div className="akte-karte-body">
            {children}
            {weitere}
          </div>
        )}
      </SectionCard>
    );
  }

  return (
    <section className={"fsec" + (open ? "" : " collapsed")}>
      <div className="fsec-h" onClick={() => setOpen((o) => !o)}>
        <button className="fsec-toggle" aria-label={open ? "Einklappen" : "Ausklappen"}>{CHEV_R}</button>
        {icon && <span className="fsec-ico">{icon}</span>}
        <h2>{title}</h2>
        {count != null && <span className="cnt">· {count} Felder</span>}
        {hasTools && (
          <span className="fsec-tools" onClick={(e) => e.stopPropagation()}>{werkzeuge}</span>
        )}
      </div>
      <div className="fsec-body">{children}</div>
    </section>
  );
}

// ---- "Daten ändern" data modal (read-only Anzeige echter Daten) -------------
const DM_ICONS: Record<string, React.ReactNode> = {
  pin: <svg width="17" height="17" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round"><path d="M9 16s5-4.2 5-8.5A5 5 0 009 2.5 5 5 0 004 7.5C4 11.8 9 16 9 16z" /><circle cx="9" cy="7.5" r="1.8" /></svg>,
  edit: <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M13.5 3.5l3 3L7 16l-3.5.5L4 13z" /></svg>,
  trash: <svg width="17" height="17" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M3.5 5h11M7 5V3.5h4V5M5.5 5l.7 9.5h5.6L12.5 5" /></svg>,
  plus: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M8 3.5v9M3.5 8h9" /></svg>,
};

// Entscheidung 75 (F6): Der Kommunikationswege-Tab im „Daten ändern“-Modal heißt
// „Kontaktdaten“ — abgegrenzt vom umbenannten Haupt-Tab „Kommunikation“.
const DM_TABS = ["Anschriften", "Kontaktdaten", "Bankverbindungen"];

type DmData = Record<string, { cols: string[]; rows: string[][]; actions: string[]; objs: any[] }>;

function DataModal({ open, tab, contactName, dmData, contactId, canWrite, reload, onClose, onTab, istFirma, firmen, kontoinhaber }: {
  open: boolean; tab: string; contactName: string; dmData: DmData; contactId: number; canWrite: boolean; reload: () => void; onClose: () => void; onTab: (t: string) => void;
  istFirma?: boolean; firmen?: Array<{ id: number; name: string }>;
  /** Name des Kontakts für eine neue Bankverbindung („Max Mustermann"). */
  kontoinhaber?: string;
}) {
  const [dialog, setDialog] = useState<{ existing?: Record<string, any> | null } | null>(null);
  if (!open) return null;
  const data = dmData[tab];
  const columns: Column<string[]>[] = [
    ...data.cols.map((col, j) => ({
      id: "c" + j,
      header: col,
      className: j === 0 ? "name" : undefined,
      cell: (r: string[]) => (r[j] === "—" ? <Dash /> : r[j]),
    })),
    {
      id: "act",
      className: "colact",
      header: "Aktion",
      cell: (_r: string[], i: number) => (
        <span className="acts-cell">
          {data.actions.map((a) => (
            <GhostIconButton
              key={a}
              title={a}
              icon={DM_ICONS[a]}
              onClick={canWrite ? () => setDialog({ existing: data.objs[i] }) : undefined}
            />
          ))}
        </span>
      ),
    },
  ];
  return createPortal(
    <div className="kvp-rd-scrim" onClick={onClose}>
      <div className="dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-ico">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinejoin="round"><rect x="8" y="3.5" width="10.5" height="10.5" rx="2.2" /><path d="M14 17.5A1.5 1.5 0 0112.5 19h-7A1.5 1.5 0 014 17.5v-7A1.5 1.5 0 015.5 9" strokeLinecap="round" /></svg>
            </div>
            <div className="dm-titles">
              <div className="dm-kicker">Daten ändern</div>
              <h2>{contactName}</h2>
            </div>
            <button className="dm-x" onClick={onClose} aria-label="Schließen">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
            </button>
          </div>
          <div className="dm-tabs">
            {DM_TABS.map((t) => (
              <button key={t} className={tab === t ? "on" : ""} onClick={() => onTab(t)}>{t}</button>
            ))}
          </div>
        </div>
        <div className="dm-body">
          <div className="dm-sec">
            <h3>{tab}</h3>
            <button className="dm-new" onClick={() => canWrite && setDialog({ existing: null })} disabled={!canWrite}>{DM_ICONS.plus}Neu</button>
          </div>
          {data.rows.length > 0
            ? <DataTable columns={columns} rows={data.rows} rowKey={(_, i) => i} />
            : <div className="empty" style={{ padding: "24px 0" }}>Keine Einträge vorhanden.</div>}
        </div>
        <div className="dm-foot">
          <button onClick={onClose} style={{ padding: "11px 22px", borderRadius: 11, border: "none", cursor: "pointer", font: '700 14px/1 "Hanken Grotesk", sans-serif', background: "#7d2169", color: "#fff" }}>Schließen</button>
        </div>
        {dialog && (
          <DataFormDialog
            tab={tab as DataTab}
            contactId={contactId}
            existing={dialog.existing}
            onClose={() => setDialog(null)}
            onSaved={() => { setDialog(null); reload(); }}
            istFirma={istFirma}
            firmen={firmen}
            kontoinhaber={kontoinhaber}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

// ---- main view --------------------------------------------------------------
// Schloss, Fenster, Minimieren — Inline-SVG wie die übrigen Kopf-Symbole.
const SCHLOSS_ZU = (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"><rect x="3" y="7" width="10" height="7" rx="1.6" /><path d="M5.2 7V5.2a2.8 2.8 0 015.6 0V7" /></svg>
);
const SCHLOSS_AUF = (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"><rect x="3" y="7" width="10" height="7" rx="1.6" /><path d="M5.2 7V5.2a2.8 2.8 0 015.4-1" /></svg>
);
const FENSTER_ICON = (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="2.5" y="4.5" width="9" height="9" rx="1.5" /><path d="M5.5 4.5V3a.5.5 0 01.5-.5h7a.5.5 0 01.5.5v7a.5.5 0 01-.5.5h-2" /></svg>
);
const MINIMIEREN_ICON = (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><path d="M3.5 12.5h9" /></svg>
);

const ContactDetailsRedesign: React.FC<{
  /** Im Fenster (WindowManager) statt aus der URL. */
  contactIdProp?: number;
  imFenster?: boolean;
  /** Id des Fensters (WindowManager) — für die Schließen-Rückfrage. */
  fensterId?: string;
}> = ({ contactIdProp, imFenster = false, fensterId }) => {
  // Zahlungsverkehr ist vorerst auf admin/gfgl beschraenkt (siehe Migration
  // 202608140005) — der Reiter wird ohne Leserecht ausgeblendet.
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const contactId = contactIdProp ?? Number(id);
  const { openWindow, minimizeWindow, setWindowCloseGuard } = useWindowManager();
  const hasContact = Number.isFinite(contactId) && contactId > 0;
  const navState = location.state as {
    k?: { name?: string; typ?: string; status?: string };
    navContext?: { orderedIds?: number[]; currentIndex?: number };
  } | null;
  const k = navState?.k;
  // Geordnete ID-Liste + Position aus der Tabelle (Paket A) für Prev/Next ohne 404.
  const orderedIds = navState?.navContext?.orderedIds ?? [];
  const currentIndex = navState?.navContext?.currentIndex ?? -1;

  // Deep-Link aus der Suche (Task 6/10): `/contacts/<id>?tab=Dokumente&doc=<id>&dq=<term>`.
  // Nur lesend — der Tab-State wird beim Klicken NICHT in die URL zurückgeschrieben.
  const [searchParams] = useSearchParams();
  const paramTab = searchParams.get("tab");
  const openDocId = Number(searchParams.get("doc")) || undefined;
  const docSearchTerm = searchParams.get("dq") ?? undefined;
  // `&ordner=<pfad>`: diesen Ordner der Kundenakte öffnen (z. B. nach dem
  // Ablegen einer Mail in einen Schaden-Ordner).
  const openOrdner = ordnerAusParam(searchParams.get("ordner"));

  const [tab, setTab] = useState(() => resolveTab(paramTab) ?? "Allgemein");

  // ⌘K-Navigation auf dieselbe (bereits offene) Kontaktseite: Param-Änderung übernehmen.
  useEffect(() => {
    const resolved = resolveTab(paramTab);
    if (resolved) setTab(resolved);
  }, [paramTab]);

  // Die Leiste scrollt horizontal, sobald das Fenster für alle Reiter zu schmal
  // ist. Wer über einen Deep-Link auf einem weit rechts liegenden Reiter landet
  // (z. B. „Kundentagebuch“ aus einer Benachrichtigung), sähe ihn sonst nicht.
  /**
   * Zugeklappter Kontaktkopf — merkt sich die Wahl über Kontaktwechsel und
   * Sitzungen hinweg. Wer ihn einmal zuklappt, will Platz für den Reiter, nicht
   * bei jedem Kontakt neu klicken. Standard: aufgeklappt.
   */
  const [kopfZu, toggleKopf] = usePersistentCollapsed(KOPF_ZU_KEY, false);
  // Kopf klebt auch auf dem Handy (T-146, damit das Schloss erreichbar bleibt)
  // und klappt sich dort beim Scrollen selbst zusammen.
  const kopfRef = React.useRef<HTMLDivElement | null>(null);
  const kopfKompakt = useKopfKompaktBeimScrollen(kopfRef, !imFenster);

  /**
   * Darstellung des Reiters „Allgemein“: die gewachsene Gliederung oder die
   * Karten der Kontakt-Anlage. Wie der Kopf-Klappzustand kontaktübergreifend
   * gemerkt — wer die Karten will, will sie überall.
   *
   * Reine Anzeige: Der Wechsel fasst kein Feld an. Genau daran hängt, dass die
   * Speicherleiste beim Umschalten ruhig bleibt.
   */
  const [ansicht, setAnsicht] = useState<AkteAnsicht>(gemerkteAnsicht);
  const ansichtWechseln = React.useCallback(() => {
    setAnsicht((vorher) => {
      const naechste = andereAnsicht(vorher);
      merkeAnsicht(naechste);
      return naechste;
    });
  }, []);

  /**
   * „Allgemein" = Anlage-Formular im Bearbeiten-Modus mit neuem Kopf und Schloss
   * (User 27.09.2026). Seit 28.09.2026 die einzige Ansicht — der Umschalter „A"
   * und die gemerkte Wahl (`kmvs.contactDetails.anlageAnsicht`) sind entfallen.
   * Die Konstante bleibt nur, damit die Kopf-Zeilen, die andere Sessions gerade
   * bearbeiten, unverändert weiterlaufen; sie ist immer `true`.
   */
  const anlageAnsicht = true as boolean;

  // Schloss im Kopf (User 28.09.2026): gesperrt = nur ansehen. Entsperrt wird
  // bearbeitet und alles zwischengespeichert; erst beim Sperren speichert die
  // Akte — Formular (Diff-PUT) und Kopf-Felder zusammen.
  const [gesperrt, setGesperrt] = useState(true);
  const [schlossLaeuft, setSchlossLaeuft] = useState(false);
  const anlageRef = React.useRef<AkteAnlageHandle | null>(null);
  const portfolioRef = React.useRef<PartnerPortfolioHandle | null>(null);
  // Synchroner Spiegel von `gesperrt`: Nach „Speichern"/„Verwerfen" im
  // Verlassen-Dialog geht es im selben Zug weiter (Reiter, Navigation) — der
  // State wäre bis zum nächsten Rendern noch „entsperrt" und fragte erneut.
  const gesperrtRef = React.useRef(gesperrt);
  gesperrtRef.current = gesperrt;
  const sperren = React.useCallback(() => {
    gesperrtRef.current = true;
    setGesperrt(true);
  }, []);

  const tabbarRef = React.useRef<HTMLDivElement | null>(null);
  const activeTabRef = React.useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const leiste = tabbarRef.current;
    const knopf = activeTabRef.current;
    if (!leiste || !knopf) return;
    if (knopf.offsetLeft >= leiste.scrollLeft &&
        knopf.offsetLeft + knopf.offsetWidth <= leiste.scrollLeft + leiste.clientWidth) {
      return; // schon sichtbar — nicht unnötig scrollen
    }
    // Nur die Leiste selbst bewegen; scrollIntoView würde die ganze Seite
    // mitziehen und den Kopfbereich wegscrollen.
    leiste.scrollTo({
      left: knopf.offsetLeft - (leiste.clientWidth - knopf.offsetWidth) / 2,
      behavior: "smooth",
    });
  }, [tab]);

  // Beim Reiter-Wechsel an den Anfang des Inhalts springen. Ohne das behält die
  // Seite die Scrollposition des vorherigen Reiters: der Kopfbereich (.dhead) ist
  // sticky und bleibt sichtbar, der Anfang des neuen Inhalts liegt aber darüber
  // und damit unsichtbar — man muss erst hochscrollen, um z. B. die Ebenen-Auswahl
  // der Kennzahlen zu sehen. Betrifft nur die vertikale Position; die horizontale
  // Reiter-Leiste regelt der Effect darüber.
  useEffect(() => {
    const scroller = findScrollParent(tabbarRef.current);
    if (scroller && scroller.scrollTop !== 0) scroller.scrollTop = 0;
  }, [tab]);

  const [dm, setDm] = useState<{ open: boolean; tab: string }>({ open: false, tab: "Anschriften" });
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [approval, setApproval] = useState<ApprovalUiState>(APPROVAL_INITIAL);

  const {
    contactData,
    setContactData,
    isDirty,
    canWrite,
    canReassignOwnership,
    anredeOptions,
    titelOptions,
    legalFormOptions,
    gesellschaftsform,
    setGesellschaftsform,
    oldLegalForm,
    setOldLegalForm,
    users,
    appTeams,
    setField,
    commValue,
    setComm,
    commValueFirma,
    setCommFirma,
    addCommFirma,
    faxValue,
    setFax,
    upsertCommunication,
    handleSave,
    assignOwnership,
    toasterId,
    reload,
    reloadWennSauber,
    rohKontakt,
    ladeFehler,
  } = useContactGeneralForm(contactId, hasContact);
  // Eigener Controller auf die Toaster-Id des Hooks — für Meldungen, die hier
  // (nicht im Hook) entstehen.
  const { dispatchToast } = useToastController(toasterId);
  // Berufsstatus kommt aus dem gepflegten Katalog (ContactBerufsstatus), nicht
  // mehr aus einer lokalen Liste. Der Ist-Wert wird unten ergänzt, falls er
  // nicht (mehr) im Katalog steht.
  const berufsstatusOptions = useBerufsstatusOptions();
  // Wer schaut gerade mit, und wer tippt in welchem Feld? Eigener Socket-Raum
  // neben dem des Kundentagebuchs (siehe useContactPresence).
  const praesenz = useContactPresence(hasContact ? contactId : null);

  const cd = contactData;
  const editable = canWrite;

  // Fehler aus den Vollmachten-Chips (PDF/Scan holen, Scan hochladen) sichtbar
  // machen — ein still gescheiterter Upload sähe sonst aus wie ein erfolgreicher.
  const meldeVollmachtFehler = React.useCallback(
    (meldung: string) => {
      dispatchToast(
        <Toast>
          <ToastTitle>Vollmachten</ToastTitle>
          <ToastBody>{meldung}</ToastBody>
        </Toast>,
        { intent: "error" },
      );
    },
    [dispatchToast],
  );
  const name = (cd ? displayContactName(cd as any) : null) || k?.name || "Kontakt";
  // Das Kreuz steht getrennt vom Namen: `name` geht auch in Titel, Suche und
  // Weitergaben — dort wäre es ein Wert, kein Hinweis.
  const istTot = istVerstorben(cd as any);
  const typ = (cd?.typ ?? cd?.type?.type ?? k?.typ ?? "Firma") as string;
  // REIHENFOLGE beachten: geladene Daten VOR `k`. `k` ist die Momentaufnahme aus
  // der Tabelle (location.state) — sie zeigt sofort etwas an, solange nichts
  // geladen ist, ändert sich danach aber NIE mehr. Stand sie vorn, überdeckte
  // sie jede Statusänderung: Der PATCH lief (HTTP 200, Datenbank geschrieben),
  // die Pille zeigte trotzdem den alten Wert. Wie bei `name` und `typ` gewinnen
  // deshalb die geladenen Daten.
  const status = ((cd?.status?.status ?? (typeof cd?.status === "string" ? cd?.status : undefined))
    ?? cd?.kontaktStatus
    ?? k?.status) as string | undefined;
  const isCompany = typ === "Firma";
  // Kontoinhaber in Kontoschreibweise, bei Firmen Name + Zusatz ohne
  // Rechtsform-Kürzel (utils/kontoinhaber.ts).
  const kontoinhaberAkte = kontoinhaberVorschlag(cd, isCompany);
  // Produktpartner und ihre Ansprechpartner behalten im Reiter „Allgemein" die
  // bisherigen, schmalen Abschnitte (kein Anlage-Formular dafür).
  const istPartnerAkte = !!cd && isPartnerStatus(status);

  // Reiter-Sichtbarkeit hängt bewusst am GELADENEN Typ, nicht an `typ`: der rät
  // oben „Firma“, solange nichts geladen ist (und `k` kann beim Prev/Next-Sprung
  // veraltet sein). Ohne diese Unterscheidung blitzte beim Direktaufruf einer
  // Privatperson kurz jeder Nur-Firma-Reiter auf und verschwände wieder.
  const tabsIsCompany = cd ? isCompany : false;

  // Ein Nur-Firma-Reiter kann aus einem Deep-Link oder aus dem Prev/Next-Sprung
  // Firma → Person stammen. Dann zurück auf „Allgemein“, sonst stünde der
  // Reiter-Inhalt ohne zugehörigen Reiter da. Erst ab geladenen Daten — sonst
  // würfe der geratene Typ einen gültigen Deep-Link weg.
  useEffect(() => {
    if (!cd) return;
    if (!isTabVisible(tab, isCompany, status)) setTab("Allgemein");
  }, [cd, tab, isCompany, status]);

  // --- Kontaktstatus: Haupt- + Nebenstatus in einer Pille ------------------
  // Nebenstatus kommen aus ContactStatusLink (Backend), die Firmennamen fürs
  // „Mensch fürs Unternehmen“-Segment aus den ohnehin geladenen Relationen.
  const extraStatuses = ((cd as { extraStatuses?: string[] } | undefined)?.extraStatuses ?? []);
  const statusCompanies = React.useMemo(
    () =>
      derivePersonEmployment(
        (cd as any)?.relationsSrc ?? (cd as any)?.relations,
        (cd as any)?.relationsDest,
      ).map((e: { companyName: string }) => e.companyName),
    [cd],
  );
  // Verbundene Firmen der Person mit Id — Datenbasis der Unternehmens-Auswahl im
  // Kontaktdaten-Dialog („geschäftlich bei …", genau 1 → immer gesetzt).
  const employmentFirmen = React.useMemo(
    () =>
      derivePersonEmployment(
        (cd as any)?.relationsSrc ?? (cd as any)?.relations,
        (cd as any)?.relationsDest,
      )
        .filter((e) => e.companyId != null)
        .map((e) => ({ id: e.companyId as number, name: e.companyName })),
    [cd],
  );
  // Nur aktive Verbindungen — ausgeschiedene Firmen stehen in der Auswahl
  // nicht zur Wahl (User 02.10.2026).
  const waehlbareFirmen = React.useMemo(
    () =>
      derivePersonEmployment(
        (cd as any)?.relationsSrc ?? (cd as any)?.relations,
        (cd as any)?.relationsDest,
        { ohneAusgeschiedene: true },
      )
        .filter((e) => e.companyId != null)
        .map((e) => ({ id: e.companyId as number, name: e.companyName })),
    [cd],
  );
  // Zentrale für die automatische Durchwahl der geschäftlichen Festnetznummer:
  // Nummern der verbundenen Firmen + gemeinsamer Anfang der eigenen
  // geschäftlichen Nummern (forms/widgets/firmenZentrale).
  const geschaeftlicheZentrale = usePersonZentrale({
    firmaIds: employmentFirmen.map((f) => f.id),
    eigeneNummern: [
      commValue("phone", kommArt("geschaeftlich")),
      ...((cd?.communications ?? []) as any[])
        .filter((c) => c?.kind === "geschaeftlich" && (c?.type === "phone" || c?.type === "fax"))
        .map((c) => c.value),
    ],
  });

  // --- Arbeitgeber (Beruf-Block) ------------------------------------------
  // Der Arbeitgeber ist eine der vorhandenen Person↔Firma-Beziehungen, keine
  // Spalte am Kontakt. Er wird deshalb NICHT über den Kontakt-PUT gespeichert,
  // sondern über seine eigene Ressource — über denselben Speichern-Knopf, wie
  // es die Branchen-Klassifikation vormacht.
  // `undefined` = unverändert; `null` = Verknüpfung lösen.
  /**
   * Nach dem Verknüpfen eines Arbeitgebers wird die Rolle abgefragt — und zwar
   * verbindlich (PO 31.08.2026). Grund: Eine Firma allein sagt nur, WO jemand
   * arbeitet; erst die Verbindung sagt, ALS WAS. Daran hängen betriebliche
   * Vorsorge, Organigramm und die Firmenzuordnung der Kommunikation.
   *
   * Der Dialog schreibt SOFORT (attach-menschen), die Arbeitgeber-Markierung
   * dagegen erst am Speichern-Knopf. Die Reihenfolge ist wichtig: Erst die
   * Verbindung, dann die Markierung — andersherum entstünde eine zweite
   * Beziehungszeile statt einer Markierung der vorhandenen.
   */
  const [rollenAbfrage, setRollenAbfrage] = useState<{ id: number; name: string } | null>(null);

  const [arbeitgeberWahl, setArbeitgeberWahl] = useState<
    { id: number; name: string } | null | undefined
  >(undefined);
  useEffect(() => setArbeitgeberWahl(undefined), [contactId]);

  const arbeitgeberKandidatenListe = React.useMemo(
    () =>
      arbeitgeberKandidaten(
        (cd as any)?.relationsSrc ?? (cd as any)?.relations,
        (cd as any)?.relationsDest,
      ),
    [cd],
  );

  // Firmen der Person („Verbindung zu Unternehmen") — zählen bei der
  // Kundenerstinformation zum Verbund („Für alle im Verbund generieren").
  const unternehmenDerPerson = React.useMemo(
    () =>
      isCompany
        ? []
        : arbeitgeberKandidatenListe.map((f) => ({ id: f.companyId as number, name: f.companyName })),
    [isCompany, arbeitgeberKandidatenListe],
  );

  const arbeitgeberBefund: ArbeitgeberBefund = React.useMemo(() => {
    const gespeichert = arbeitgeberErmitteln(
      (cd as any)?.relationsSrc ?? (cd as any)?.relations,
      (cd as any)?.relationsDest,
      (cd?.arbeitgeber ?? "") as string,
    );
    if (arbeitgeberWahl === undefined) return gespeichert;
    if (arbeitgeberWahl === null) {
      const frei = String(cd?.arbeitgeber ?? "").trim();
      return frei ? { art: "freitext", name: frei } : { art: "keiner" };
    }
    // Die frisch gewählte Firma hat noch keine Beziehung — Name aus der Auswahl,
    // damit die Maske sofort zeigt, was gleich gespeichert wird.
    // `istHauptarbeitgeber` immer true: Die Wahl IST die ausdrückliche Markierung,
    // auch wenn der Kandidat aus den Beziehungen sie noch nicht trägt. Sonst
    // verschwände direkt nach dem Auswählen der „lösen"-Knopf, der genau diese
    // Markierung wieder zurücknimmt.
    const bekannt = arbeitgeberKandidatenListe.find((f) => f.companyId === arbeitgeberWahl.id);
    return {
      art: "verknuepft",
      firma: {
        ...(bekannt ?? {
          companyId: arbeitgeberWahl.id,
          companyName: arbeitgeberWahl.name,
          roles: [],
          isExtern: false,
          istMenschImUnternehmen: true,
        }),
        istHauptarbeitgeber: true,
      },
    };
  }, [cd, arbeitgeberWahl, arbeitgeberKandidatenListe]);

  const [statusJump, setStatusJump] = useState<{ anchorId: string; nonce: number } | null>(null);
  const jumpToSection = (target: StatusJumpTarget) =>
    setStatusJump((prev) => ({
      anchorId: target === "haushalt" ? ANCHOR_HAUSHALT : ANCHOR_VERBINDUNGEN,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
  const changePrimaryStatus = async (next: { statusName: string; weitereStatus: string[] }) => {
    if (!contactId) return;
    // Wechselt der Status zwischen Kunde/Interessent … und Produktpartner, baut
    // die Akte „Allgemein" in einer anderen Gestalt neu auf — ungespeicherte
    // Eingaben des Formulars wären weg (Befund 28.09.2026). Vorher fragen.
    let gespeichert = false;
    if (tab === "Allgemein" && isPartnerStatus(next.statusName) !== isPartnerStatus(status)) {
      const vorher = ungespeichertJetzt();
      if (!(await verlassenErlaubt())) return;
      gespeichert = vorher;
    }
    try {
      // Hauptstatus + Rollen in einem Zug (Popup an der Pille, 28.09.2026):
      // der Endpoint gleicht die Zusatz-Status-Links ab und liefert den
      // fertigen Stand zurück.
      const { data } = await apiFetch<{ status: string; extraStatuses: string[] }>(
        `contact/${contactId}/kontaktstatus`,
        {
          method: "PUT",
          // Ohne Content-Type schickt der Browser text/plain; Nest parst den
          // Body dann nicht und @Body() bleibt leer — bei HTTP 200.
          headers: { "Content-Type": "application/json" },
          // `bisher`: was diese Akte kannte — eine Rolle, die ein Kollege
          // inzwischen vergeben hat, wird so nicht mit abgewählt.
          body: JSON.stringify({ ...next, bisher: bisherigeStatus(cd) }),
        },
      );
      // Direkt im UI übernehmen statt neu zu laden: die Seite springt sonst an
      // den Anfang zurück und verliert Scrollposition und offene Abschnitte.
      setContactData((c) =>
        c
          ? ({
              ...c,
              status: { ...(typeof c.status === "object" ? c.status : {}), status: data.status },
              extraStatuses: data.extraStatuses,
            } as typeof c)
          : c,
      );
      // Das Speichern davor hat einen Neuladen angestoßen, dessen Antwort den
      // alten Status tragen kann — einmal nach dem Statuswechsel nachladen.
      if (gespeichert) reload();
    } catch (e) {
      // Nicht verschlucken: ein fehlgeschlagenes Speichern sähe sonst genauso
      // aus wie ein erfolgreiches — nur ohne Wirkung.
      dispatchToast(
        <Toast>
          <ToastTitle>Kontaktstatus nicht gespeichert</ToastTitle>
          <ToastBody>{String((e as { message?: string })?.message ?? e)}</ToastBody>
        </Toast>,
        { intent: "error" },
      );
    }
  };

  const emailAddresses = ((cd?.communications ?? []) as Array<{ type?: string; value?: string }>)
    .filter((cm) => cm.type === "email" && cm.value)
    .map((cm) => cm.value as string);

  const initials = typ === "Firma"
    ? (name.replace(/[^A-Za-zÄÖÜ ]/g, "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("") || "K")
    : (name.replace(/[^A-Za-zÄÖÜ ,]/g, "").split(/[,\s]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("") || "K");

  // --- Foto-Upload (Logo/Avatar) -------------------------------------------
  const photoInputRef = React.useRef<HTMLInputElement | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  /** Profilbild aus LinkedIn/Xing — nur für Personen, siehe ProfilbildDialog. */
  const [profilbildOffen, setProfilbildOffen] = useState(false);
  // Bearer-only-Vorbereitung: Foto authentifiziert als blob: laden (statt nacktem
  // <img src="/api/media/...">, das am Cookie hinge). useBlobUrl trägt den Bearer.
  /**
   * Ein eben über „Daten befüllen" bestätigtes Logo (Formular im Reiter
   * „Allgemein"). Es ist sofort gespeichert, die Akte lädt aber NICHT neu —
   * das setzte das eingebettete Formular samt ungespeicherter Eingaben zurück.
   * Bis zum nächsten Laden zeigt der Kopf deshalb dieses Bild.
   */
  const [logoLokal, setLogoLokal] = useState<string | null>(null);
  useEffect(() => setLogoLokal(null), [cd?.photo_url]);
  const photoRef = logoLokal ?? cd?.photo_url ?? null;
  const onPhotoPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !contactId) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await fetch(apiUrl("upload/contact-photo"), { method: "POST", body: fd });
      if (!up.ok) throw new Error("upload failed");
      const { url } = await up.json();
      const patch = await fetch(apiUrl(`contact/${contactId}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ photo_url: url }),
      });
      // Eigene Änderung, kein Konflikt fürs Formular (utils/kontaktStand.ts).
      if (patch.ok) eigenenStandMelden(contactId, (await patch.json().catch(() => null))?.version);
      reload();
    } catch {
      /* Foto-Upload-Fehler bleibt still — Nutzer kann erneut versuchen */
    } finally {
      setPhotoUploading(false);
    }
  };

  const INTERNAL_TO_LABEL: Record<string, string> = {
    allgemein: "Allgemein", metrics: "Kennzahlen", risiken: "Risiken", vertraege: "Verträge",
    schaeden: "Schäden", vorgaenge: "Vorgänge", mail: "Kommunikation", diary: "Kundentagebuch",
    // "analyse" zeigt auf den Risiken-Reiter: der eigene Analyse-Reiter ist
    // wieder dort aufgegangen, alte interne Sprungziele bleiben gültig.
    documents: "Dokumente", analyse: "Risiken",
  };

  const openDM = (t: string) => setDm({ open: true, tab: t });
  const goToContact = (targetId: number) => {
    if (targetId < 1) return;
    // navContext mit aktualisierter Position weiterreichen, damit Prev/Next über
    // mehrere Sprünge funktioniert.
    if (orderedIds.length > 0) {
      const nextIndex = orderedIds.indexOf(targetId);
      navigate(`/contacts/${targetId}`, {
        state: { k, navContext: { orderedIds, currentIndex: nextIndex } },
      });
      return;
    }
    navigate(`/contacts/${targetId}`);
  };

  // --- field-binding shortcuts (onChange nur wenn editable → sonst read-only) ---
  const fval = (key: keyof ContactDetailsFormValues) => (cd?.[key] ?? "") as string;
  // Dokumente: welche Rückfrage und welche Felder die gewählte Art zeigt.
  const unterart = UNTERARTEN[fval("idCardType")];
  const dokFelder = dokumentFelder(fval("idCardType"), fval("idCardSubtype"));
  const klassen = dokFelder.klassen;
  /**
   * Nur die Präsenz-Bindung eines Feldes — für Felder mit eigenem onChange.
   * Gemeldet wird ausschließlich der Feldschlüssel, nie der Inhalt.
   */
  const fpres = (fieldKey: string) => ({
    onFocus: editable ? () => praesenz.feldFokus(fieldKey) : undefined,
    onBlur: editable ? () => praesenz.feldBlur(fieldKey) : undefined,
    lockedBy: praesenz.bearbeitetVon(fieldKey),
  });
  /**
   * Bindet ein Feld ans Formular UND an die Live-Präsenz; wird gespreadet
   * (`{...fon("plz")}`), damit jede Feldinstanz beides ohne eigene Verdrahtung
   * bekommt.
   */
  const fon = (key: keyof ContactDetailsFormValues) => ({
    onChange: editable
      ? (v: string) => (key === "plz" || key === "ort" ? setPlzOderOrt(key, v) : setField(key, v))
      : undefined,
    ...fpres(String(key)),
  });
  /**
   * PLZ oder Ort ändern — ein Ortsteil, der nicht mehr passt, fällt dabei weg
   * (utils/ortsteilAbgleich.ts, User 30.09.2026).
   */
  function setPlzOderOrt(key: "plz" | "ort", v: string) {
    const aenderung = key === "plz" ? { plz: v } : { ort: v };
    if (ortsteilLeeren({ plz: fval("plz"), ort: fval("ort"), ortsteil: fval("ortsteil") }, aenderung)) {
      setField("ortsteil", "");
    }
    setField(key, v);
  }
  const dateVal = (key: keyof ContactDetailsFormValues) => ((cd?.[key] ?? "") as string).slice(0, 10);
  const janein = (key: keyof ContactDetailsFormValues): string => {
    const raw = cd?.[key];
    return raw == null ? "" : raw ? "Ja" : "Nein";
  };
  const onJanein = (key: keyof ContactDetailsFormValues) => ({
    onChange: editable ? (v: string) => setField(key, v === "" ? null : v === "Ja") : undefined,
    ...fpres(String(key)),
  });
  /**
   * Wie `fon`, aber für `date`-Spalten: Ein geleertes Datumsfeld liefert "",
   * und "" ist für Postgres kein Datum (22007) — die Löschung des Wertes muss
   * als `null` gehen, sonst scheitert das Speichern der ganzen Maske.
   */
  const fonDatum = (key: keyof ContactDetailsFormValues) => ({
    onChange: editable ? (v: string) => setField(key, v === "" ? null : v) : undefined,
    ...fpres(String(key)),
  });
  /**
   * Wie `fon`, aber für Spalten mit geschlossenem Wertevorrat (CHECK-Constraint):
   * Die Leer-Option eines Selects heißt „nicht erfasst" und muss als `null`
   * gehen. Als "" bräche sie den CHECK — bei `taxClass` (`CHK_Contact_taxClass`
   * erlaubt nur NULL oder I–VI) scheiterte damit das Speichern der GANZEN Maske,
   * nicht nur dieses einen Feldes, und die einmal gesetzte Steuerklasse ließe
   * sich nie wieder entfernen.
   */
  const fonVokabular = (key: keyof ContactDetailsFormValues) => ({
    onChange: editable ? (v: string) => setField(key, v === "" ? null : v) : undefined,
    ...fpres(String(key)),
  });

  // --- Branchen-Klassifikation (nur Firmen) --------------------------------
  // Seit 08.09.2026 zwei Ebenen: Branche gemäß KMVS → Branchengruppe. Der
  // dreistufige Altkatalog wird von hier weder gelesen noch geschrieben — er
  // bleibt bestehen, weil `brancheClassId` und die eingefrorenen Antwortsätze
  // der Risikoanalyse darauf zeigen, und die beiden Kataloge zählen ihre Ids
  // unabhängig. Die Klassifikation läuft über denselben Speichern-Knopf wie
  // die Stammdaten.
  // `tabsIsCompany` statt `isCompany`: letzteres rät „Firma", solange nichts
  // geladen ist — sonst liefe für jede Privatperson ein überflüssiger Abruf.
  const branchen = useBranchengruppen(
    contactId,
    tabsIsCompany,
    (cd as { branchengruppeId?: number | null } | null)?.branchengruppeId ?? null,
    (cd as { brancheFreitext?: string | null } | null)?.brancheFreitext ?? null,
  );

  const onBranchenChange = (next: BrancheGruppenWahl[]) => {
    branchen.setAuswahl(next);
    // Die Hauptbranche (erste Zeile) bleibt zusätzlich am Kontakt: Listen,
    // Suche und Auswertungen brauchen sie ohne Join. Beide Felder sind keine
    // gebundenen Maskenfelder, reisen aber über den Spread in
    // apiToForm/formToApi mit (siehe kontaktSpeicherPayload.branchen.test.ts).
    setField("branchengruppeId", next[0]?.branchengruppeId ?? null);
    setField("brancheFreitext", branchen.freitextVon(next));
    // `industry` (Freitext) NUR füllen, wenn es leer ist. Die 28 Bestandswerte
    // („Militär", „Vermögen haltung", …) sind unnormalisiert, aber keine
    // Duplikate der Klassifikation — sie stillschweigend zu überschreiben wäre
    // Datenverlust.
    const pfad = branchen.pfadVon(next);
    if (pfad && !String(cd?.branche ?? "").trim()) setField("branche", pfad);
  };

  /** Freitext-Branche aus dem Altbestand — angezeigt, solange sie etwas sagt. */
  const brancheAltwert = String(fval("branche") ?? "").trim();
  const zeigeBrancheAltwert =
    !!brancheAltwert && brancheAltwert !== branchen.pfadVon(branchen.auswahl);

  // --- Bestandsanzeige: Einordnung aus dem dreistufigen Altkatalog ----------
  // Firmen, die vor der Umstellung eingeordnet wurden, tragen eine
  // `brancheClassId`. Die neue Auswahl kennt sie nicht (anderer Id-Raum) —
  // ohne diese Zeile verschwände die bisherige Branche aus der Akte, ohne
  // dass jemand sie angefasst hätte. Das wäre den Anwendern nicht erklärbar.
  // Nur lesend: Gepflegt wird ab jetzt oben, der Altwert bleibt als Herkunft
  // stehen, bis die Firma neu eingeordnet ist.
  const { rows: altKatalog } = useBrancheClassification();
  const brancheAltId =
    (cd as { brancheClassId?: number | null } | null)?.brancheClassId ?? null;
  const brancheAltPfad = brancheAltId
    ? formatBranchePath(findLeaf(altKatalog, brancheAltId))
    : "";


  // --- Rechtsform: aktualisiert Rechtsvorgänger mit dem vorherigen Wert ---
  const onLegalForm = !editable ? undefined : (v: string) => {
    const selected = legalFormOptions.find((lf) => lf.legalForm === v);
    const prevLabel = gesellschaftsform;
    const prevSelected = legalFormOptions.find((lf) => lf.legalForm === prevLabel);
    setGesellschaftsform(v);
    setContactData((c) => {
      if (!c) return c;
      const nd: ContactDetailsFormValues = { ...c, legalForm: selected ?? null };
      if (prevSelected && prevLabel && prevLabel !== v) {
        setOldLegalForm(prevLabel);
        nd.oldLegalForm = prevSelected;
      }
      return nd;
    });
  };
  const onOldLegalForm = !editable ? undefined : (v: string) => {
    const selected = legalFormOptions.find((lf) => lf.legalForm === v);
    setOldLegalForm(v);
    setContactData((c) => (c ? { ...c, oldLegalForm: selected ?? null } : c));
  };
  const onVorname = !editable ? undefined : (v: string) =>
    setContactData((c) => (c ? { ...c, vorname: v, firstName: v } : c));
  const onZweck = !editable ? undefined : (v: string) =>
    setContactData((c) => (c ? { ...c, defaultBankAccount: { ...(c.defaultBankAccount || {}), bankDescription: v } } : c));

  // --- "Daten ändern"-Modal: echte Daten (read-only) ---
  const dmData: DmData = useMemo(() => {
    const dash = (x: unknown) => (x == null || x === "" ? "—" : String(x));
    const addresses = (cd?.addresses ?? []) as any[];
    const comms = (cd?.communications ?? []) as any[];
    // Socials werden separat im Social-Abschnitt gepflegt → hier nicht als Kommunikation listen.
    const commsNonSocial = comms.filter((c) => !(SOCIAL_PLATFORMS as readonly string[]).includes(c.type));
    const banks = (cd?.bankAccounts ?? []) as any[];
    return {
      Anschriften: {
        // „Land" ergänzt: Dies ist die einzige Stelle, an der alle Anschriften
        // eines Kontakts nebeneinander stehen — und einer Auslandsadresse war
        // dort nicht anzusehen, dass sie eine ist.
        cols: ["Bezeichnung", "Straße", "Ort", "Ortsteil", "PLZ", "Land", "Standard"],
        // `a.type` stand hier als Rückfall und war IMMER leer: Die Spalte gab es
        // nicht, der Payload schickte sie zwar mit, TypeORM verwarf sie. Seit
        // 09/2026 trägt `addressType`, was die Anschrift ist.
        rows: addresses.map((a) => [dash(anschriftBezeichnung(a)), dash(a.street), dash(a.city), dash(a.ortsteil), dash(a.postalCode), dash(a.country), a.isDefault ? "Ja" : "—"]),
        actions: ["edit", "trash"],
        objs: addresses,
      },
      Kontaktdaten: {
        cols: ["Bezeichnung", "Typ", "Wert", "Standard"],
        rows: commsNonSocial.map((c) => [dash(c.label || c.type), dash(c.type), dash(c.value), c.isDefault ? "Ja" : "—"]),
        actions: ["edit", "trash"],
        objs: commsNonSocial,
      },
      Bankverbindungen: {
        // „Zweck" (Spalte bankDescription) steht im Bearbeiten-Dialog schon
        // immer zur Verfügung, war in der Übersicht aber unsichtbar — bei
        // mehreren Konten sah man dort nicht, welches wofür da ist.
        // cols und rows greifen positionsgleich ineinander (cell: r[j]);
        // eine Spalte ohne die zugehörige Zelle verschiebt alles Folgende.
        cols: ["Bank", "IBAN", "BIC", "Kontoinhaber", "Zweck"],
        rows: banks.map((b) => [
          dash(b.bankName),
          dash(formatIbanForDisplay(String(b.iban ?? ""))),
          dash(b.bic),
          dash(b.accountHolder),
          dash(b.bankDescription),
        ]),
        actions: ["edit", "trash"],
        objs: banks,
      },
    };
  }, [cd]);

  // --- Kontaktverantwortlichkeit (FO/BO) — Pillen im Band `.verantrow` ---
  // Das Band liegt als eigene Zeile zwischen Titelzeile und Chip-Zeile und ist
  // für alle vier Layouts dasselbe, deshalb stehen FO und BO nur noch hier und
  // nicht mehr als Formularfelder in den Sektionen.
  const foValue = cd?.foUserId ?? "";
  // Der aktuelle FO bleibt wählbar, auch wenn er den Abteilungs-/Rollenfilter
  // nicht (mehr) erfüllt — sonst verschwände die bestehende Zuordnung aus der Liste.
  const foUsers = users.filter((u) => ((u.department ?? "").split("+").includes("FO") && u.role !== "admin") || u.id === foValue);
  const boTeamValue = cd?.boType === "TEAM" ? (cd?.boTeamId ?? "") : "";
  // Alt-Daten kennen einen BO vom Typ USER. Wählbar ist er hier nicht (der
  // Endpoint nimmt NONE/TEAM), angezeigt wird er trotzdem — sonst stünde bei
  // einem gesetzten BO fälschlich „Kein BO-Team".
  const boUserName = cd?.boType === "USER"
    ? (users.find((u) => u.id === (cd?.boUserId ?? ""))?.name || "Unbekannter Nutzer")
    : "";
  const boName = boTeamValue
    ? (appTeams.find((tm) => tm.id === boTeamValue)?.name || "Unbekanntes Team")
    : boUserName;
  // Ein Alt-BO vom Typ USER hat keine Team-Id, ist aber gesetzt. Ohne Platzhalter
  // wäre „Kein BO-Team" die scheinbar aktuelle Wahl und damit nicht mehr klickbar
  // — der Alt-Eintrag ließe sich nicht entfernen.
  const boSelected = boTeamValue || (boUserName ? "bo-alt-user" : "");

  // --- gemeinsame Sektionen ---
  const kontaktnummern = () => (
    <Sec title="Kontaktnummern" icon={SEC_ICONS.num} defaultOpen={false}>
      <div className="fgrid">
        <Text label="Interne Kontaktnummer" value={fval("contactNbrInt")} {...fon("contactNbrInt")} ph="Interne Kontaktnummer" />
        <Text label="Allfinanz Kontaktnummer" value={fval("contactNbrAllfinanz")} {...fon("contactNbrAllfinanz")} ph="Allfinanz Kontaktnummer" />
        <Text label="Externe Kontaktnummer" value={fval("contactNbrExt")} {...fon("contactNbrExt")} ph="Externe Kontaktnummer" />
      </div>
    </Sec>
  );

  const anschrift = (streetLabel: string) => {
    // Die Felder oben führen ausschließlich die STANDARD-Anschrift; alles
    // Weitere lag bisher nur im Verwalten-Dialog und war in der Maske
    // unsichtbar. In der Kartenansicht sagt es die Zeile am Kartenfuß.
    const mehr = weitereAnschriften(cd?.addresses);
    return (
    // Der Abschnittstitel bleibt mit dem oeffnenden Sec-Tag auf EINER Zeile:
    // privatkontaktStruktur.test.ts liest ihn per Regex aus dem Quelltext.
    <Sec title="Anschrift" icon={SEC_ICONS.pin}
      tools={[{ label: "Anschriften verwalten", count: cd?.addresses?.length ?? 0, icon: STACK_ICON, onClick: () => openDM("Anschriften") }]}
      weitere={
        <WeitereZeile
          anzeigenText={weitereText(mehr.anzahl, "weitere Anschrift", "weitere Anschriften")}
          ausblendenText="Weitere Anschriften ausblenden"
        >
          <WeitereEintraege
            eintraege={mehr.eintraege}
            hinweis="Geändert werden weitere Anschriften über „Anschriften verwalten“."
          />
        </WeitereZeile>
      }
    >
      <div className="fgrid">
        {/* Land zuerst: es entscheidet, wie der Rest zu lesen ist. Kein
            Auto-„Deutschland" — sonst wäre ein Kontakt ohne Landangabe schon
            beim Öffnen „dirty". */}
        {/* Ohne Präsenz-Signal: CountrySelect/NationalitySelect/FinanzamtSelect
            sind derselbe geteilte SearchableSelect, der weder `onFocus`/`onBlur`
            durchreicht noch einen Platz für die Einblendung hat. Eine Hülle mit
            `onFocusCapture` wie bei TagInput scheidet aus — sie bräuchte ein
            eigenes `.fld` um das Widget, und `.kvp-rd .fld label` überschriebe
            dessen Label-Optik. Nachziehbar, sobald der Widget-Baustein Fokus-
            Callbacks bekommt. */}
        {editable ? (
          <CountrySelect
            value={fval("land")}
            onChange={(v) => setField("land", v)}
            autoDefault={false}
          />
        ) : (
          <Text label="Land" value={fval("land")} ph="Land" />
        )}
        <Text label={streetLabel} value={fval("strasse")} {...fon("strasse")} ph={streetLabel} />
        <Text label="PLZ" value={fval("plz")} {...fon("plz")} ph="PLZ" />
        <Text label="Ort" value={fval("ort")} {...fon("ort")} ph="Ort" />
        <Text label="Ortsteil" value={fval("ortsteil")} {...fon("ortsteil")} ph="Ortsteil" />
      </div>
    </Sec>
    );
  };

  const kommunikationTools = () => ({
    toolNodes: (
      <AvailabilityButton
        contactId={contactId}
        value={cd?.erreichbarkeit}
        onUpdate={(v) => setField("erreichbarkeit", v)}
        disabled={!editable}
      />
    ),
    tools: [{ label: "Kommunikationswege verwalten", count: cd?.communications?.length ?? 0, icon: STACK_ICON, onClick: () => openDM("Kontaktdaten") }],
  });

  const socialRow = () => (
    <div className="social-row">
      {SOCIAL_ROW_PLATFORMS.map((platform) => (
        <SocialChannel
          key={platform}
          platform={platform}
          existing={(cd?.communications || []).find((c: any) => c.type === platform)}
          contactId={contactId}
          onSave={upsertCommunication}
          disabled={!editable}
        />
      ))}
    </div>
  );

  /**
   * Alle weiteren Kommunikationswege (PO-Notiz 02.08.: „bei Contactdetails alle
   * Kommunikationsmittel anzeigen").
   *
   * Die festen Felder darüber zeigen je Art genau EINEN Wert — die zweite
   * Zentrale, das Diensthandy oder die Nummer beim Arbeitgeber blieben unsichtbar
   * und waren nur über „Kommunikationswege verwalten" zu finden. Hier stehen sie,
   * mit ihrer Bezeichnung und — bei einer geschäftlichen Nummer einer Person —
   * dem Namen der Firma, zu der sie gehört. Anrufen und Mailen geht direkt.
   *
   * Bewusst nur lesend: geändert wird an EINER Stelle (dem Verwalten-Dialog),
   * sonst gäbe es zwei Wahrheiten für dieselbe Zeile.
   */
  const weitereKommunikation = (bereitsGezeigt: (c: any) => boolean) => {
    const comms = (cd?.communications ?? []) as any[];
    const rest = comms.filter(
      (c) =>
        c?.value &&
        !SOCIAL_PLATFORMS.includes(c.type) &&
        !bereitsGezeigt(c),
    );
    if (rest.length === 0) return null;
    const beschriftung = (c: any) => {
      const art = TYP_LABEL[c.type as string] ?? c.type;
      // Alle Unternehmen des Wegs (02.10.2026), als „A und B".
      const firma =
        c.kind === "geschaeftlich" ? firmenVoll(firmenDerZeile(c, employmentFirmen)) || null : null;
      const zusatz = [c.label, firma].filter(Boolean).join(", ");
      return zusatz ? `${art} (${zusatz})` : art;
    };
    const block = (
      <div className="fgrid" style={{ marginTop: 18 }}>
        {rest.map((c) => (
          <ActField
            key={c.id ?? `${c.type}-${c.value}`}
            label={beschriftung(c)}
            value={c.value}
            action={c.type === "email" ? "mail" : "phone"}
          />
        ))}
      </div>
    );
    // In der klassischen Ansicht stehen die Zusatzwege wie bisher offen unter
    // den festen Feldern. In der Karte klappen sie hinter eine Zeile — sonst
    // wird aus der Karte bei einem Kontakt mit vielen Nummern eine Liste.
    if (ansicht !== "karten") return block;
    return (
      <WeitereZeile
        anzeigenText={weitereText(rest.length, "weiterer Kommunikationsweg", "weitere Kommunikationswege")}
        ausblendenText="Weitere Kommunikationswege ausblenden"
      >
        {block}
      </WeitereZeile>
    );
  };

  /** Wert eines geplanten Kommunikationsfeldes (Bindung steht im Plan). */
  const kommWert = (plan: KommFeldPlan): string => {
    if (plan.bindung === "geteilt") return commValue(plan.typ, kommArt(plan.spalte));
    if (plan.bindung === "fax") return faxValue;
    return commValueFirma("homepage");
  };

  const kommSetzen = (plan: KommFeldPlan, wert: string) => {
    if (plan.bindung === "geteilt") setComm(plan.typ, kommArt(plan.spalte), wert);
    else if (plan.bindung === "fax") setFax(wert);
    else setCommFirma("homepage", wert);
  };

  // Person: zwei feste Spalten — links privat, rechts geschäftlich. Firma:
  // einfache Felder (dort gibt es die Unterscheidung nicht).
  // `mitSocial` trennt die Social-Media-Zeile ab: In der klassischen Ansicht
  // steht sie oben in der Kommunikation, in der Kartenansicht in einer eigenen
  // Karte — so führt es die Kontakt-Anlage.
  const kommunikationPerson = ({ mitSocial = true } = {}) => {
    const t = kommunikationTools();
    // Unternehmen hinter einem geschäftlichen Feld — gekürzt, voller Text im Tooltip.
    const firmenAmFeld = (plan: KommFeldPlan) =>
      firmenAnzeige(firmaFuerGeschaeftlichesFeld(cd?.communications, plan.typ, employmentFirmen));
    return (
      <Sec title="Kommunikation" icon={SEC_ICONS.comm} toolNodes={t.toolNodes} tools={t.tools}>
        {mitSocial && socialRow()}
        <KommunikationSpalten
          feld={(plan) => (
            <ActField
              label={plan.label}
              // Die Firma steht nur da, wo sie belegt ist — siehe
              // firmaFuerGeschaeftlichesFeld: erst die am Kommunikationsweg
              // gespeicherte, sonst die einzige verbundene, sonst gar keine.
              firma={plan.mitFirma ? firmenAmFeld(plan).text || undefined : undefined}
              firmaTitel={plan.mitFirma ? firmenAmFeld(plan).voll || undefined : undefined}
              value={kommWert(plan)}
              onChange={editable ? (v) => kommSetzen(plan, v) : undefined}
              geschaeftlich={plan.spalte === "geschaeftlich"}
              zentrale={plan.spalte === "geschaeftlich" && plan.typ === "phone" ? geschaeftlicheZentrale : undefined}
              {...fpres(plan.presenceKey)}
              action={plan.action}
              ph={plan.ph}
              maxLength={plan.bindung === "homepage" ? WEB_URL_MAX_LENGTH : undefined}
            />
          )}
        />
        {/* Alles, was die festen Felder oben nicht fassen: zweite Nummern,
            Diensthandys, Nummern beim Arbeitgeber — mit Bezeichnung und Firma. */}
        {weitereKommunikation((c) => {
          const wert = String(c.value ?? "");
          return KOMM_FELDER_PERSON.some((plan) => kommWert(plan) === wert);
        })}
      </Sec>
    );
  };

  /** Die vier festen Kommunikationswege einer Firma. */
  const firmaKommFelder = () => (
    <>
      <ActField label="Telefon" value={commValueFirma("phone")} onChange={editable ? (v) => setCommFirma("phone", v) : undefined} {...fpres("comm:phone")} action="phone" ph="Telefon" geschaeftlich />
      <ActField label="Mobil" value={commValueFirma("mobile")} onChange={editable ? (v) => setCommFirma("mobile", v) : undefined} {...fpres("comm:mobile")} action="phone" ph="Mobil" geschaeftlich />
      <ActField label="Telefax" value={commValueFirma("fax")} onChange={editable ? (v) => setCommFirma("fax", v) : undefined} {...fpres("comm:fax")} action="phone" ph="Telefax" geschaeftlich zentrale={commValueFirma("phone")} />
      <ActField label="E-Mail" value={commValueFirma("email")} onChange={editable ? (v) => setCommFirma("email", v) : undefined} {...fpres("comm:email")} action="mail" ph="E-Mail" />
      {/* Die Website steht nicht mehr hier, sondern oben bei den
          Allgemeinen Informationen unter dem Namenszusatz. Sie bleibt
          derselbe Kommunikationsweg (`homepage`) — in „weitere
          Kommunikationswege" taucht sie trotzdem nicht auf, weil der Filter
          dort alle SOCIAL_PLATFORMS ausschließt und `homepage` dazugehört. */}
    </>
  );

  /**
   * Zweite Zentrale, Support-Nummer, Abteilungs-Mail — alles, was die vier
   * festen Felder nicht fassen.
   */
  const weitereKommunikationFirma = () =>
    weitereKommunikation((c) => {
      const wert = String(c.value ?? "");
      return [
        commValueFirma("phone"), commValueFirma("mobile"),
        commValueFirma("fax"), commValueFirma("email"),
      ].includes(wert);
    });

  const kommunikationFirma = ({ mitSocial = true } = {}) => {
    const t = kommunikationTools();
    return (
      <Sec title="Kommunikation" icon={SEC_ICONS.comm} toolNodes={t.toolNodes} tools={t.tools}>
        {mitSocial && socialRow()}
        <div className="fgrid">{firmaKommFelder()}</div>
        {weitereKommunikationFirma()}
      </Sec>
    );
  };

  /**
   * Was aus der IBAN ableitbar ist, wird eingetragen — aber nur in LEERE
   * Felder. Ein bereits erfasster BIC bleibt stehen: bei Bankfusionen ist der
   * abweichende BIC gelegentlich bewusst so hinterlegt, und stillschweigend
   * korrigierte Bankdaten sind schlimmer als lückenhafte.
   */
  const bankAusIbanUebernehmen = (bank: BankTreffer) => {
    if (!editable) return;
    setContactData((c) => {
      if (!c) return c;
      const leer = (v: unknown) => !String(v ?? "").trim();
      const nd = { ...c };
      let geaendert = false;
      if (bank.bic && leer(c.bic)) {
        nd.bic = bank.bic;
        geaendert = true;
      }
      if (leer(c.kreditinstitut)) {
        nd.kreditinstitut = bank.bankName;
        geaendert = true;
      }
      return geaendert ? nd : c;
    });
  };

  const bank = () => {
    // Wie bei der Anschrift: Die Felder führen nur das STANDARD-Konto.
    const mehr = weitereBankverbindungen(cd?.bankAccounts);
    return (
    <Sec title="Bank" icon={SEC_ICONS.bank}
      tools={[{ label: "Bankverbindungen verwalten", count: cd?.bankAccounts?.length ?? 0, icon: STACK_ICON, onClick: () => openDM("Bankverbindungen") }]}
      weitere={
        <WeitereZeile
          anzeigenText={weitereText(mehr.anzahl, "weitere Bankverbindung", "weitere Bankverbindungen")}
          ausblendenText="Weitere Bankverbindungen ausblenden"
        >
          <WeitereEintraege
            eintraege={mehr.eintraege}
            hinweis="Geändert werden weitere Bankverbindungen über „Bankverbindungen verwalten“."
          />
        </WeitereZeile>
      }
    >
      <div className="fgrid">
        <Text label="Bank" value={fval("kreditinstitut")} {...fon("kreditinstitut")} ph="Bank" />
        <IbanFeld label="IBAN" value={fval("iban")} {...fon("iban")} ph="DE00 0000 0000 0000 0000 00" onBankErkannt={bankAusIbanUebernehmen} />
        <Text label="BIC" value={fval("bic")} {...fon("bic")} ph="BIC" />
        <Text label="Zweck" value={cd?.defaultBankAccount?.bankDescription ?? ""} onChange={onZweck} {...fpres("bankDescription")} ph="Zweck" />
        {/* Vorbelegt mit dem Namen des Kontakts; gespeichert wird er mit der
            Bankverbindung (kontaktSpeicherPayload). Ein eigener Eintrag bleibt. */}
        <Text label="Kontoinhaber" value={fval("accountHolder") || kontoinhaberAkte} {...fon("accountHolder")} ph="Kontoinhaber" />
        <Sel label="Bankeinzug" value={janein("directDebit")} {...onJanein("directDebit")} options={JANEIN} ph="— wählen —" />
      </div>
    </Sec>
    );
  };

  // --- Vertretung und Bevollmächtigter (nur Personen) ----------------------
  // Zwei Blickrichtungen in EINEM Abschnitt (PO-Vorgabe): oben die Verfügungen,
  // die FÜR diesen Kontakt existieren, darunter die Rollen, die er FÜR ANDERE
  // ausübt. Der Abschnitt steht bewusst als LETZTER im Allgemein-Tab — er ist
  // Beratungswissen, kein Stammdatum, das man beim Anlegen braucht.
  const verfuegungen: VerfuegungForm[] = useMemo(
    () => verfuegungenAusApi(cd?.verfuegungen),
    [cd?.verfuegungen],
  );
  // Vollmachten, die Courté über den Kontakt hält (Kundenerstinformation,
  // Maklervollmacht …): Status, PDF, Upload der unterschriebenen Fassung und
  // der öffentliche Signierlink. Speichert jede Aktion sofort — unabhängig von
  // der Speicherleiste des Reiters.
  const vollmachtenSec = () => (
    <Sec title="Vollmachten" icon={SEC_ICONS.legal}>
      <VollmachtenAkteInhalt
        contactId={contactId}
        nurLesen={!editable}
        kontaktName={name}
        unternehmen={unternehmenDerPerson}
      />
    </Sec>
  );

  const vertretungUndVollmacht = () => (
    <Sec title="Vorsorge- und Nachlassdokumente" icon={SEC_ICONS.legal}>
      <div className="fgrid">
        {/* Dreiwertig wie „Vorsteuerabzugsberechtigung“ und „Gemeinnützig“:
            leer = nicht erfasst, und das ist etwas anderes als „Nein“. */}
        <Sel
          label="Vorsorge- oder Nachlassdokumente vorhanden?"
          value={janein("vollmachtenVorhanden")}
          {...onJanein("vollmachtenVorhanden")}
          options={JANEIN}
          ph="— wählen —"
        />
      </div>
      {/* Bei „Nein“ und bei „nicht erfasst“ bleibt die Liste weg — dort ist
          nichts weiter zu erfassen (vertretungLogik.zeigeVerfuegungen). */}
      {zeigeVerfuegungen(cd?.vollmachtenVorhanden) && (
        <div style={{ marginTop: 16 }}>
          <VerfuegungenListe
            value={verfuegungen}
            onChange={(next) => setField("verfuegungen", next)}
            editable={editable}
            selbstId={cd?.kontaktId}
            onOpenContact={goToContact}
          />
        </div>
      )}
      {/* `reloadWennSauber` statt `reload`: Die Vertretungen speichern sofort,
          die Verfügungen darüber erst am Speichern-Knopf. Ein voller Refetch
          nach dem Anlegen einer Vertretung würde die eben angekreuzten
          Verfügungen und die getippten Bevollmächtigten-Namen wegwerfen — und
          zwar die aus DERSELBEN Karte. Bleibt der Refetch aus, schreibt die
          Liste ihre eigene Änderung lokal fort. */}
      <VertretungenListe
        contactId={contactId}
        relationsDest={(cd as any)?.relationsDest}
        canWrite={editable}
        onChanged={reloadWennSauber}
        onOpenContact={goToContact}
      />
    </Sec>
  );

  /** Label der aktuell GEWÄHLTEN Anrede — nicht das der geladenen. */
  const anredeLabelAktuell =
    anredeOptions.find((o) => String(o.id) === String(fval("anrede") ?? ""))?.salutation ??
    (cd as any)?.salutation?.salutation ??
    undefined;
  const geschlechtAktuell: string | undefined =
    geschlechtAusAnrede(anredeLabelAktuell) || (cd as any)?.geschlecht || (cd as any)?.gender;

  /**
   * T3 (Themen 01.10.2026): „Verheiratet"/„Zusammenlebend" gewählt, aber
   * kein lebender (Ehe-)Partner unter den Beziehungen → Popup „Ehepartner
   * erfassen"; auf Wunsch öffnet der Beziehungsdialog mit vorbelegtem Label.
   * Beide Richtungen zählen — die Partner-Arten sind symmetrisch.
   * Seit 02.10.2026 auch „Geschieden" → „Ex-Partner erfassen", dann als
   * Beziehung außerhalb des Haushalts.
   */
  const [partnerVorlage, setPartnerVorlage] = useState<{ label: string; imHaushalt: boolean } | null>(null);
  const beiFamilienstandAuswahl = async (v: string) => {
    const art = partnerFrageFuerStatus(v);
    if (!art || !cd?.kontaktId) return;
    const zeilen = [
      ...(((cd as any)?.relationsSrc ?? (cd as any)?.relations ?? []) as any[]).map((r) => ({ r, gegenueber: r?.contactDest })),
      ...(((cd as any)?.relationsDest ?? []) as any[]).map((r) => ({ r, gegenueber: r?.contactSrc })),
    ];
    const beziehungen = zeilen
      .filter(({ r }) => r && r.mergedIntoRelationId == null)
      .map(({ r, gegenueber }) => ({
        label: r.relationType?.contactRelationType as string | undefined,
        verstorben: gegenueber?.deceased === true,
      }));
    if (hatLebendenPartner(art, beziehungen)) return;
    const label = await partnerErfassenFragen(
      art,
      genderOf({ gender: geschlechtAktuell, salutationLabel: anredeLabelAktuell }),
    );
    // Ex-Partner (nach „Geschieden", 02.10.2026) nicht im Haushalt.
    if (label) setPartnerVorlage({ label, imHaushalt: partnerImHaushalt(art) });
  };

  // Geschlecht/Anrede für den Beziehungs-Dialog (Gegenrichtung „was ist dieser
  // Kontakt für die andere Person?"). Seit 08/2026 wird das Geschlecht nicht
  // mehr eigens erfasst, sondern folgt der Anrede — deshalb hat die gerade
  // gewählte Anrede hier Vorrang vor dem gespeicherten Wert: Wer die Anrede
  // eben von „Herr" auf „Frau" gestellt hat, soll im Beziehungsdialog nicht
  // noch „Schwager" angeboten bekommen. Erst danach greifen der Formularwert
  // und der Rohwert aus der API.
  const connections = () => (
    <ContactConnections
      contactId={cd?.kontaktId}
      relations={cd?.relationsSrc}
      relationsDest={(cd as any)?.relationsDest}
      group={cd?.group as any}
      contactType={typ}
      self={{
        id: cd?.kontaktId,
        name,
        typ,
        status,
        gender: geschlechtAktuell,
        salutationLabel: anredeLabelAktuell,
        // Die eigene Rechtsform entscheidet, welche Rollen an dieser Firma
        // vergeben werden dürfen — und ob beim Verbinden erst eine Rechtsform
        // erfasst werden muss. Sie lag geladen bereit, wurde aber nie
        // weitergereicht.
        legalForm: (cd as any)?.legalForm?.legalForm ?? undefined,
        legalFormId: (cd as any)?.legalForm?.id ?? undefined,
      }}
      canWrite={canWrite}
      onChanged={reload}
      jumpTo={statusJump}
    />
  );

  const nameError = showErrors && !String(cd?.lastname ?? "").trim()
    ? "Pflichtfeld – darf nicht leer sein"
    : undefined;

  // Ein Verein hat keinen „Firmennamen". Die Beschriftung folgt der GEWÄHLTEN
  // Rechtsform — `gesellschaftsform` ist der Bearbeitungsstand, der Text
  // wechselt also schon beim Umstellen der Auswahl, nicht erst nach dem
  // Speichern (utils/koerperschaftBegriff.ts). Die Rechtsform selbst geht mit:
  // die eGbR steht im Gesellschafts-, nicht im Handelsregister (T-329).
  const koerperschaft = koerperschaftTexte(
    unternehmensBegriffAus(legalFormOptions, null, gesellschaftsform),
    rechtsformOptionAus(legalFormOptions, null, gesellschaftsform) ?? {
      legalForm: gesellschaftsform,
    },
  );

  // Welche Felder der Steuer-/Rechtsdaten überhaupt gelten, entscheidet
  // dieselbe Ableitung wie die Beschriftungen (utils/koerperschaftBegriff.ts).
  // Beide Regeln bekommen die aktuellen Werte mit, damit ein gepflegtes Feld
  // nie hinter der Anzeigeregel verschwindet — siehe dort.
  const zeigeGemeinnuetzigkeit = fuehrtGemeinnuetzigkeit(koerperschaft, {
    istGemeinnuetzig: cd?.istGemeinnuetzig,
    freistellungsbescheidBis: dateVal("freistellungsbescheidBis"),
    zweckNrAO: fval("zweckNrAO"),
    satzungszweck: fval("satzungszweck"),
  });
  const zeigeFreistellung = zeigeFreistellungsbescheid(
    cd?.istGemeinnuetzig,
    dateVal("freistellungsbescheidBis"),
  );
  const zeigeRegister = zeigeRegisterFeld(
    koerperschaft,
    fval("handelsregisterNummer"),
    fval("registergericht"),
  );

  // --- Firma-Layout ---
  /**
   * Mitgliedschaften und Bemerkung als eigene Bausteine — sie stehen in der
   * klassischen Ansicht in „Allgemeine Informationen“, in der Kartenansicht in
   * eigenen Karten (so führt es die Kontakt-Anlage). Dieselben Felder, zwei
   * Plätze: deshalb einmal geschrieben statt zweimal.
   */
  const mitgliedschaftenFelder = () =>
    editable ? (
      <FeldPraesenzHuelle className="fld span2" {...fpres("mitgliedschaften")}>
        <TagInput label="Mitgliedschaften" name="mitgliedschaften" value={fval("mitgliedschaften")} onChange={(v) => setField("mitgliedschaften", v)} placeholder="Mitgliedschaften" />
      </FeldPraesenzHuelle>
    ) : (
      <Text label="Mitgliedschaften" value={fval("mitgliedschaften")} ph="Mitgliedschaften" span />
    );

  const bemerkungFeld = (ph: string) => (
    <TextArea label="Bemerkung" value={fval("remark")} {...fon("remark")} ph={ph} span />
  );

  // --- Ansprache (nur Personen) -------------------------------------------
  // Steht weit oben, weil der Block spätere SELEKTIONEN ersetzt: Wer hier
  // vorgemerkt ist, muss vor der nächsten Veranstaltung nicht herausgesucht
  // werden. Dieselben Felder wie in der Anlage — beide Masken zeigen dasselbe.
  const zuwendungen: ZuwendungItem[] = useMemo(
    () => zuwendungenAusApi(cd?.zuwendungen),
    [cd?.zuwendungen],
  );
  const anspracheSec = () => (
    <Sec title="Ansprache" icon={SEC_ICONS.info}>
      <div className="fgrid">
        <Sel
          label="Einzuladen zu Courté-Veranstaltungen"
          value={fval("einladungKennzeichen")}
          {...fon("einladungKennzeichen")}
          options={EINLADUNGS_KENNZEICHEN.map((o) => o.value)}
          blankLabel="Nicht vorgemerkt"
        />
        <Sel label="Newsletter" value={janein("newsletter")} {...onJanein("newsletter")} options={JANEIN} ph="— wählen —" />
      </div>
      {/* Zuwendungen, Geschenke und Einladungen in EINER Liste: dieselben
          Angaben, dieselben Auswertungsfragen. Die Liste ist dieselbe
          Komponente wie in der Anlage. */}
      {editable || zuwendungen.length > 0 ? (
        <ZuwendungenListe
          value={zuwendungen}
          nurLesen={!editable}
          onChange={editable ? (next) => setField("zuwendungen", next as any) : () => {}}
        />
      ) : null}
    </Sec>
  );

  // Der Abschnitt heißt seit 09/2026 „Vereinszugehörigkeiten und
  // Mitgliedschaften" (PO). Das Freitextfeld bleibt — es trägt den Bestand —,
  // aber der bessere Weg steht daneben: Ein Verein IST ein Firmenkontakt
  // (Rechtsform e.V.), und die Mitgliedschaft ist eine Verknüpfung mit Amt.
  // Nur so taucht die Person auch in der Akte des Vereins auf und die
  // Zugehörigkeit überlebt jede Umbenennung.
  const mitgliedschaftenSec = () => (
    <Sec title="Vereinszugehörigkeiten und Mitgliedschaften" icon={SEC_ICONS.info}>
      <div className="fgrid">{mitgliedschaftenFelder()}</div>
      <div className="fld-hint">
        Für einen Verein die <b>Verknüpfung</b> unter „Verbindungen" nutzen: Dort
        lässt sich das Amt wählen (Vorstand, Schriftführer, Mitglied …), und der
        Kontakt erscheint in der Akte des Vereins.
      </div>
    </Sec>
  );

  const bemerkungSec = (ph: string) => (
    <Sec title="Bemerkung" icon={SEC_ICONS.info}>
      <div className="fgrid">{bemerkungFeld(ph)}</div>
    </Sec>
  );

  /** Social Media als eigene Karte — in der klassischen Ansicht Teil der Kommunikation. */
  const socialSec = () => (
    <Sec title="Social Media" icon={SEC_ICONS.comm}>
      {socialRow()}
    </Sec>
  );

  /* Der Rechtsvorgänger wird von der Rechtsform-Auswahl automatisch befüllt. */
  const rechtsvorgaengerFeld = () => (
    <Sel label="Rechtsvorgänger" value={oldLegalForm} onChange={onOldLegalForm} {...fpres("oldLegalForm")} options={legalFormOptions.map((lf) => lf.legalForm)} blankLabel="keine" />
  );

  /**
   * Die Firma als Rechtsperson: Name, Rechtsform, Anschrift im Netz.
   *
   * Eigener Baustein, weil die beiden Ansichten ihn verschieden umgeben. Die
   * klassische Ansicht führt ihn in „Allgemeine Informationen"; die
   * Kartenansicht stellt ihn an den Anfang EINER Karte, in der — wie in der
   * Kontakt-Anlage — auch Branche, Steuerdaten und Kommunikation stehen.
   *
   * `mitRechtsvorgaenger`: Das Feld wird von der Rechtsform-Auswahl automatisch
   * befüllt und gehört deshalb neben sie. In der klassischen Ansicht steht es
   * seit jeher weiter unten bei den Wirtschaftsdaten — dort bleibt es, damit
   * sich die bestehende Ansicht nicht ungefragt umsortiert.
   */
  const firmaIdentitaetFelder = ({ mitRechtsvorgaenger = false } = {}) => (
    <>
      <Text label={koerperschaft.nameLabel} value={fval("lastname")} {...fon("lastname")} ph={koerperschaft.nameLabel} error={nameError} />
      <Sel label="Rechtsform" value={gesellschaftsform} onChange={onLegalForm} {...fpres("legalForm")} options={legalFormOptions.map((lf) => lf.legalForm)} ph="— wählen —" />
      {mitRechtsvorgaenger && rechtsvorgaengerFeld()}
      {/* Nur im Firma-Zweig: die Fortsetzung eines abgeschnittenen Firmennamens,
          zur Anzeige mit einem Leerzeichen an ihn gehängt. Personen führen ihren
          abweichenden Namen stattdessen als Rufname. Der Zusatz steht allein in
          seiner Zeile, aber halbbreit — über die ganze Karte gezogen fiele das
          kurze Feld optisch aus der Reihe. */}
      <Text label="Namenszusatz (Alt)" value={fval("suffix")} {...fon("suffix")} ph="Fortsetzung des Firmennamens" />
      {/* Die Website steht bei den Stammdaten, nicht unten bei den
          Kommunikationswegen: Sie ist die Adresse, unter der man die Firma
          nachschlägt, und der Ausgangspunkt für Impressumsabgleich und
          Firmendaten-Anreicherung. Gespeichert wird sie unverändert als
          Kommunikationsweg `homepage` — es ist dasselbe Feld, nur an einem
          nützlicheren Platz. Der Knopf daneben öffnet sie im neuen Tab. */}
      <ActField label="Website" value={commValueFirma("homepage")} onChange={editable ? (v) => setCommFirma("homepage", v) : undefined} {...fpres("comm:homepage")} action="web" ph="www.muster-gmbh.de" maxLength={WEB_URL_MAX_LENGTH} />
    </>
  );

  /**
   * Was die Firma wirtschaftlich kennzeichnet: Stichtage und die drei
   * Ja/Nein-Angaben, an denen Steuer- und Bilanzpflichten hängen.
   */
  const firmaWirtschaftFelder = ({ mitRechtsvorgaenger = true } = {}) => (
    <>
      <DateF label="Gründungsdatum" value={dateVal("gruendungsdatum")} {...fon("gruendungsdatum")} />
      {/* „Geschäftsjahr" (bis 13.09.2026 „Geschäftsjahresbeginn"): gemeint ist
          weiterhin der Beginn (PO 19.08.2026), nur die Beschriftung ist kürzer.
          Die Spalte heißt weiterhin financialYearEnd — nur der Text wandert. */}
      <GeschaeftsjahrField label="Geschäftsjahr" value={fval("financialYearEnd")} {...fon("financialYearEnd")} />
      {mitRechtsvorgaenger && rechtsvorgaengerFeld()}
      <Sel label="Vorsteuerabzugsberechtigung" value={janein("vorsteuerAbzugsberechtigt")} {...onJanein("vorsteuerAbzugsberechtigt")} options={JANEIN} ph="— wählen —" />
      {/* Beide Spalten gibt es seit je in der Datenbank, sie waren nur nie
          bedienbar. Sie stehen hier, weil „Größenklasse bestimmen" sie
          vorschlägt — ein Vorschlag in ein unsichtbares Feld wäre wertlos. */}
      <Sel label="Bilanzierungspflicht" value={janein("bilanzierungspflichtig")} {...onJanein("bilanzierungspflichtig")} options={JANEIN} ph="— wählen —" />
      <Sel label="Prüfungspflicht (§ 316 HGB)" value={janein("pruefungspflichtHgb")} {...onJanein("pruefungspflichtHgb")} options={JANEIN} ph="— wählen —" />
    </>
  );

  /**
   * Allgemeine Informationen einer Firma — KLASSISCHE Ansicht.
   *
   * Nur dort: Die Kartenansicht baut ihre eigene, größere Fassung aus denselben
   * Bausteinen (`allgemeinFirmaKarte`) und führt Mitgliedschaften und Bemerkung
   * als eigene Karten, weil die Kontakt-Anlage das auch tut.
   */
  const allgemeinFirma = () => (
    <Sec title="Allgemeine Informationen" icon={SEC_ICONS.info}>
      <div className="fgrid">
        {firmaIdentitaetFelder()}
        {firmaWirtschaftFelder()}
        {mitgliedschaftenFelder()}
        {bemerkungFeld("Freie Notiz zu diesem Kontakt")}
      </div>
    </Sec>
  );

  // Eigener Abschnitt direkt hinter dem Allgemeinen — aufgeklappt: ein
  // zugeklappter Vorgängerversuch endete bei null Zuordnungen.
  // Der Abschnitt heißt „Branche und Tätigkeit", weil hier alles steht, worauf
  // die Risikobetrachtung fußt — und weil der Reiter „Risiken" denselben
  // Abschnitt unter demselben Namen führt. Beim Verein heißt er „Ausrichtung
  // und Schwerpunkt" (User 01.10.2026, `KoerperschaftTexte.branche`).
  const brancheTaetigkeit = () => (
    <Sec title={koerperschaft.branche.abschnittTitel} icon={SEC_ICONS.branche}>
      <div className="fgrid">{firmaBrancheFelder()}</div>
    </Sec>
  );

  /**
   * Was die Firma tut — Einstieg in die Risikoanalyse, Verbundfunktion,
   * Branchen-Klassifikation und die Beschreibung des Betriebs.
   *
   * In der klassischen Ansicht ein eigener Abschnitt „Branche und Tätigkeit"
   * (so heißt er auch im Reiter „Risiken"); in der Kartenansicht Teil der
   * einen Karte „Allgemeine Informationen" — dort steht er in der
   * Kontakt-Anlage ebenfalls.
   */
  const firmaBrancheFelder = () => (
    <>
        {/* Der Einstieg in die Risikoanalyse steht ÜBER den Feldern, die sie
            befüllt: der Fragebogen liefert Tätigkeit und Betriebscharakter,
            also gehört er hierher und nicht in den Reiter „Risiken", wo er
            bis zuletzt als große Kachel stand. */}
        <div className="fld span2">
          <AnalyseButton contactId={contactId} onRefresh={reload} />
        </div>
        {/* Wer im Verbund was tut — vor der Branche, weil die Funktion
            entscheidet, welche Risiken bei dieser Firma überhaupt liegen. */}
        <FeldPraesenzHuelle className="fld span2" {...fpres("unternehmensfunktion")}>
          <OptionChips
            label="Unternehmensfunktion"
            name={`unternehmensfunktion-${contactId}`}
            options={UNTERNEHMENSFUNKTIONEN}
            value={fval("unternehmensfunktion")}
            onChange={editable ? (v) => setField("unternehmensfunktion", v) : undefined}
            hint="Mehrfachauswahl — eine Firma kann Betrieb führen und zugleich Immobilien halten."
          />
        </FeldPraesenzHuelle>
        {/* Branche = Klassifikation aus dem Zwei-Ebenen-Katalog: eine Branche
            gemäß KMVS, darunter beliebig viele Branchengruppen. Gespeichert
            wird je Gruppe eine Zeile in ContactBranchengruppeZuordnung — mit
            dem Speichern-Knopf unten, nicht mit einem eigenen. „Sonstiges"
            blendet ein Freitextfeld ein; die sieben „Sonstige …"-Gruppen des
            Katalogs tun das ausdrücklich NICHT. */}
        <FeldPraesenzHuelle className="fld span2" {...fpres("branchen")}>
          <BrancheZweiSelect
            value={branchen.auswahl}
            onChange={onBranchenChange}
            editable={editable}
            idBase={`branchengruppe-${contactId}`}
            begriffe={koerperschaft.branche}
          />
          {branchen.ladefehler && (
            <span className="fld-err">Branchen konnten nicht geladen werden: {branchen.ladefehler}</span>
          )}
        </FeldPraesenzHuelle>
        {/* Die frühere Einordnung aus dem dreistufigen Katalog. Sie
            verschwindet nicht, nur weil die Maske jetzt zwei Ebenen führt —
            eine Firma, deren Branche ohne Zutun aus der Akte verschwände,
            wäre für die Anwender ein Fehler, kein Fortschritt. Nur lesend. */}
        {brancheAltPfad && (
          <div className="fld span2">
            <label>Bisher klassifiziert als</label>
            <input className="inp" value={brancheAltPfad} readOnly />
            <span style={{ font: "500 12px/1.35 var(--ui)", color: "var(--muted, #6b5a66)" }}>
              Aus dem früheren, dreistufigen Branchenkatalog. Bleibt zur
              Nachvollziehbarkeit stehen — gepflegt wird oben.
            </span>
          </div>
        )}
        {/* Eine Beschreibung für den ganzen Betrieb, direkt unter der
            Klassifikation (PO 17.08.2026). Vorher hing sie je Branchen-Zeile
            in der Auswahl darüber; wer Handel UND Werkstatt führte, schrieb
            dasselbe Geschäft mehrfach auf. Das Feld ist `jobOccupation` —
            dasselbe, in das die Anlage schon bisher geschrieben hat. */}
        <div className="fld span2">
          <TextArea
            label="Tätigkeitsbeschreibung"
            value={fval("taetigkeit")}
            {...fon("taetigkeit")}
            ph="Was macht der Betrieb konkret? (z. B. Reparatur von Baumaschinen, kein Neuwagenhandel)"
            rows={6}
            span
            aufzaehlung
          />
        </div>
        {/* Der alte Freitext verschwindet nicht — er trägt Bestand, den die
            Klassifikation nicht abbildet. Er ist nur nicht mehr die Pflege-
            stelle: read-only, mit einem Weg ihn zu verwerfen, sobald die
            Einordnung steht. */}
        {zeigeBrancheAltwert && (
          <div className="fld span2">
            <label>Branche (Freitext, Altbestand)</label>
            <input className="inp" value={brancheAltwert} readOnly />
            {/* Inline statt eigener Klasse: contactRedesign.css ist geteilt und
                wird in dieser Welle von mehreren Änderungen angefasst. */}
            <span style={{ font: "500 12px/1.35 var(--ui)", color: "var(--muted, #6b5a66)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              Nicht mehr gepflegt — maßgeblich ist die Klassifikation darüber.
              {editable && (
                <button
                  type="button"
                  onClick={() => setField("branche", "")}
                  style={{ border: "none", background: "transparent", padding: 0, font: "600 12px/1.35 var(--ui)", color: "var(--aub, #7d2169)", textDecoration: "underline", cursor: "pointer" }}
                >
                  Freitext verwerfen
                </button>
              )}
            </span>
          </div>
        )}
    </>
  );

  const steuerRecht = () => (
    <Sec title="Steuer- & Rechtsdaten" icon={SEC_ICONS.legal}>
      <div className="fgrid">{firmaSteuerFelder()}</div>
    </Sec>
  );

  /**
   * Steuernummern, Finanzamt, Gemeinnützigkeit und Register.
   *
   * Klassisch ein eigener Abschnitt, in der Kartenansicht Teil der Karte
   * „Allgemeine Informationen" — die Kontakt-Anlage führt dieselben Felder
   * ebenfalls dort.
   */
  const firmaSteuerFelder = () => (
    <>
        <Text label="Steuernummer" value={fval("steuernummer")} {...fon("steuernummer")} ph="z. B. 12/345/67890" />
        <Text label="Umsatzsteuer-IdNr." value={fval("umsatzsteuernummer")} {...fon("umsatzsteuernummer")} ph="z. B. DE123456789" />
        {editable ? (
          <FinanzamtSelect
            label="Zuständiges Finanzamt"
            name="finanzamt"
            value={fval("finanzamt")}
            onChange={(v) => setField("finanzamt", v)}
            // Die Amtsnummer wandert unsichtbar mit: Sie identifiziert das
            // Amt, wo der Name es nicht tut (fünfmal „München
            // Gewinneinkünfte"). Bei Freitext ohne Treffer wird sie geleert,
            // damit Name und Nummer nie auseinanderlaufen.
            onBufa={(bufa) =>
              setField("finanzamtBufa" as keyof ContactDetailsFormValues, bufa ?? null)
            }
          />
        ) : (
          <Text label="Zuständiges Finanzamt" value={fval("finanzamt")} ph="z. B. Köln-Nord" />
        )}
        {/* Das Finanzamt steht allein in seiner Zeile — ohne diesen Platzhalter
            rutschte die Handelsregisternummer neben es und das Registerpaar
            unten wäre auseinandergerissen. */}
        <div className="fld" aria-hidden />
        {/* Gemeinnützigkeit: nur für die Körperschaften, die sie tragen können
            und in deren Akte sie regelmäßig gepflegt wird. Sie ist ein
            STEUERstatus (§§ 51–68 AO) und steht deshalb hier und nicht bei den
            Allgemeinen Informationen. */}
        {zeigeGemeinnuetzigkeit && (
          <>
            <div className="span2" style={{ font: "800 11.5px/1.2 var(--ui)", letterSpacing: ".05em", textTransform: "uppercase", color: "var(--ink-3)" }}>
              Gemeinnützigkeit
            </div>
            <Sel label="Gemeinnützig (§§ 51–68 AO)" value={janein("istGemeinnuetzig")} {...onJanein("istGemeinnuetzig")} options={JANEIN} ph="— wählen —" />
            {/* Nur bei „Ja": Ohne anerkannte Gemeinnützigkeit gibt es keinen
                Freistellungsbescheid, dessen Gültigkeit man erfassen könnte.
                Steht schon ein Datum drin, bleibt es sichtbar — sonst wäre es
                nach dem Umschalten auf „Nein" weder zu sehen noch zu löschen,
                ginge beim Speichern aber unverändert mit. */}
            {zeigeFreistellung ? (
              <DateF label="Freistellungsbescheid gültig bis" value={dateVal("freistellungsbescheidBis")} {...fonDatum("freistellungsbescheidBis")} />
            ) : (
              <div className="fld" aria-hidden />
            )}
            <AoZweckeFeld
              label="Steuerbegünstigte Zwecke (§§ 52–54 AO)"
              value={fval("zweckNrAO")}
              onChange={editable ? (csv) => setField("zweckNrAO", csv) : undefined}
              idBase={`ao-zweck-${contactId}`}
              {...fpres("zweckNrAO")}
            />
            {/* Der Satzungszweck FÜHRT, die Codes darüber klassifizieren ihn
                nur (§ 52 Abs. 2 S. 2 AO lässt weitere Zwecke zu, und
                Berufsverbände kommen im Katalog gar nicht vor). */}
            <TextArea
              label="Satzungszweck"
              value={fval("satzungszweck")}
              {...fon("satzungszweck")}
              ph="Wörtlich aus der Satzung: Zweck UND Art seiner Verwirklichung (§ 60 AO)"
              rows={3}
              span
            />
          </>
        )}
        {/* Register zuletzt und in EINER Zeile: erst die Nummer, dann das
            Gericht — so steht es in Registerauszug und Impressum. Die
            Beschriftung folgt der Rechtsform (Vereins-/Genossenschafts-
            register); bei der Stiftung entfällt das Paar, solange nichts
            drinsteht — gleiches Muster wie beim Branchen-Altwert oben:
            gepflegte Daten dürfen nicht unsichtbar werden. */}
        {zeigeRegister && (editable ? (
          <>
            <Text label={koerperschaft.registerNummerLabel} value={fval("handelsregisterNummer")} {...fon("handelsregisterNummer")} ph={koerperschaft.registerPlatzhalter} />
            <AmtsgerichtSel label="Zuständiges Amtsgericht" value={fval("registergericht")} {...fon("registergericht")} />
          </>
        ) : (
          <Text
            label={koerperschaft.registerLabel}
            value={formatHandelsregister({ registergericht: fval("registergericht"), handelsregisterNummer: fval("handelsregisterNummer"), register: fval("register") })}
            ph="—"
            span
          />
        ))}
        {/* Der Abdruck lässt sich nur über Nummer UND Gericht eindeutig
            anfordern — ohne beides bleibt der Link weg, statt in eine
            Fehlermeldung zu laufen. */}
        {String(fval("handelsregisterNummer") ?? "").trim() &&
        String(fval("registergericht") ?? "").trim() ? (
          <HandelsregisterAbdruck
            contactId={contactId}
            onReload={reload}
            // Der Vertrag schreibt in die MASKE, nicht in die Datenbank —
            // gespeichert wird über den normalen Speichern-Knopf, damit die
            // Sachbearbeitung das letzte Wort behält.
            // Die Nummer aus der MASKE, nicht aus der Datenbank: Geprüft
            // werden soll, was gerade im Feld steht.
            ustId={fval("umsatzsteuernummer")}
            taetigkeit={fval("taetigkeit")}
            // Der Vorschlag wird zur HAUPTBRANCHE (erste Zeile); bereits
            // gewählte bleiben dahinter stehen. Über denselben Weg wie die
            // Auswahl von Hand, damit Katalog-Zuordnung und
            // `branchengruppeId` nicht auseinanderlaufen. Die id kommt seit
            // dem Zwei-Ebenen-Vorschlag aus `ContactBranchengruppe` — nie
            // mehr aus dem Altkatalog.
            onBranche={({ id }) =>
              onBranchenChange([
                { branchengruppeId: id, freitext: null },
                ...branchen.auswahl.filter((a) => a.branchengruppeId !== id),
              ])
            }
            onFelder={(werte) => {
              for (const { feld, wert } of werte) {
                // Die beiden HGB-Kennzeichen sind Wahrheitswerte, keine
                // Texte — „Ja" in eine boolean-Spalte zu schreiben liefe erst
                // beim Speichern auf einen Fehler.
                const jaNein = feld === "bilanzierungspflichtig" || feld === "pruefungspflichtHgb";
                setField(
                  feld as keyof ContactDetailsFormValues,
                  jaNein ? wert === "Ja" : wert,
                );
              }
            }}
          />
        ) : null}
    </>
  );

  /**
   * „Allgemeine Informationen" einer Firma in der KARTENANSICHT — eine Karte,
   * so aufgebaut wie dieselbe Karte in der Kontakt-Anlage (PO 4. September
   * 2026): Name und Rechtsform, Internet, dann Unternehmensfunktion, Branche
   * und Tätigkeit, danach Stichtage und Steuerdaten, zuletzt die
   * Kommunikationswege.
   *
   * Die klassische Ansicht verteilt genau dieselben Felder auf vier Abschnitte
   * („Allgemeine Informationen", „Branche und Tätigkeit", „Steuer- &
   * Rechtsdaten", „Kommunikation") und bleibt davon unberührt — beide Fassungen
   * bestehen aus denselben Bausteinen, keine Feldliste existiert zweimal.
   *
   * Die Werkzeuge der Kommunikation („Kommunikationswege verwalten") wandern
   * mit an den Kartenkopf: Die Felder stehen jetzt hier, also muss auch ihr
   * Verwalten-Dialog von hier erreichbar sein.
   */
  const allgemeinFirmaKarte = () => {
    const t = kommunikationTools();
    return (
      <Sec title="Allgemeine Informationen" icon={SEC_ICONS.info} toolNodes={t.toolNodes} tools={t.tools}>
        <div className="fgrid">
          {firmaIdentitaetFelder({ mitRechtsvorgaenger: true })}
          {firmaBrancheFelder()}
          {firmaWirtschaftFelder({ mitRechtsvorgaenger: false })}
          {firmaSteuerFelder()}
          {firmaKommFelder()}
        </div>
        {weitereKommunikationFirma()}
      </Sec>
    );
  };

  /**
   * „Wie kommt der Kontakt zustande?" — dieselbe Frage wie in der
   * Kontakt-Anlage, mit demselben Vokabular (`utils/kontaktherkunft`).
   *
   * Die Antwort steht seit 09/2026 als `Contact.kontaktherkunft` in der
   * Datenbank. Vorher blieb von ihr nur die WIRKUNG übrig — der
   * Empfehlungsgeber als Name, Tippgeber und Kundenverbindung als Verknüpfung
   * —, und die Akte konnte die Frage nicht beantworten.
   *
   * Die Namen selbst stehen bewusst NICHT hier: Empfehlungsgeber, Tippgeber
   * und Kundenverbindung sind Verknüpfungen (seit 09/2026 legt die Anlage sie
   * als Beziehung an, siehe `herkunft/herkunftBezug.ts`). Ein
   * zweiter Platz für dieselbe Angabe hieße, sie an zwei Stellen pflegen zu
   * können — der Hinweis sagt stattdessen, wo sie steht.
   */
  const herkunftSec = () => {
    const auswahl = herkunftZerlegen(fval("kontaktherkunft"));
    const setzeHerkunft = (v: string) => {
      setField("kontaktherkunft", v || null);
      // Der Freitext gehört zu „Sonstiges". Wird die Herkunft abgewählt, geht er
      // mit — sonst stünde er weiter in der Spalte und käme beim nächsten
      // Anhaken unerwartet zurück.
      if (!herkunftZerlegen(v).includes(HERKUNFT_SONSTIGES) && fval("kontaktherkunftSonstiges")) {
        setField("kontaktherkunftSonstiges", null);
      }
    };
    return (
      <Sec title="Wie kommt der Kontakt zustande?" icon={SEC_ICONS.info}>
        <div className="fgrid">
          <FeldPraesenzHuelle className="fld span2" {...fpres("kontaktherkunft")}>
            <OptionChips
              label="Herkunft"
              name={`kontaktherkunft-${contactId}`}
              options={HERKUNFT_OPTIONEN}
              value={fval("kontaktherkunft") ?? ""}
              onChange={editable ? setzeHerkunft : undefined}
              hint="Mehrfachauswahl. Empfehlungsgeber, Tippgeber und Kundenverbindung stehen unter „Verknüpfungen“."
            />
          </FeldPraesenzHuelle>
          {auswahl.includes(HERKUNFT_SONSTIGES) && (
            <Text
              label="Sonstiges"
              value={fval("kontaktherkunftSonstiges")}
              {...fon("kontaktherkunftSonstiges")}
              ph="Bitte angeben"
              maxLength={255}
              span
            />
          )}
        </div>
      </Sec>
    );
  };

  const allgemeinProduktpartner = () => (
    <Sec title="Allgemeine Informationen" icon={SEC_ICONS.info}>
      <div className="fgrid">
        <Text label="Name der Gesellschaft" value={fval("lastname")} {...fon("lastname")} ph="Name der Gesellschaft" error={nameError} />
        <Text label="Kurzform" value={fval("kurzname")} {...fon("kurzname")} ph="z. B. GED Leben" />
      </div>
    </Sec>
  );

  const ansprechpartnerSec = () => (
    <Sec title="Ansprechpartner" icon={SEC_ICONS.info}>
      <PartnerAnsprechpartnerListe partnerContactId={contactId} />
    </Sec>
  );

  // --- Ansprechpartner-Layout (Person mit Status „Ansprechpartner Produktpartner") ---
  const allgemeinAp = () => (
    <Sec title="Allgemeine Informationen" icon={SEC_ICONS.info}>
      <div className="fgrid">
        <Sel label="Anrede" value={fval("anrede")} {...fon("anrede")} options={anredeOptions.map((o) => ({ value: String(o.id), label: o.salutation ?? "" }))} ph="Anrede wählen" />
        <Sel label="Titel" value={fval("titel")} {...fon("titel")} options={titelOptions.map((o) => ({ value: String(o.id), label: o.title ?? "" }))} ph="- Kein Titel -" />
        <Text label="Nachname" value={fval("lastname")} {...fon("lastname")} ph="Nachname" error={nameError} />
        <Text label="Vorname (gemäß Personalausweis)" value={fval("vorname")} onChange={onVorname} {...fpres("vorname")} ph="Vorname (gemäß Personalausweis)" />
        <Text label="Rufname" value={fval("rufname")} {...fon("rufname")} ph="Rufname" />
        <DateF label="Geburtsdatum" value={dateVal("birthDate")} {...fon("birthDate")} />
      </div>
    </Sec>
  );

  // Eigener Endpunkt, eigene Tabelle — deshalb eigener Speichern-Knopf.
  const gesellschaftZuordnung = () => (
    <Sec title="Gesellschaft und Zuordnung" icon={SEC_ICONS.info}>
      <PartnerProfilSektion contactId={contactId} canWrite={editable} />
    </Sec>
  );

  /**
   * Allgemeine Informationen einer Person.
   *
   * Die Bemerkung steht hier nur in der klassischen Ansicht; die Kartenansicht
   * führt sie als eigene Karte — wie die Kontakt-Anlage.
   */
  const allgemeinPerson = ({ mitBemerkung = true, wieAnlage = false } = {}) => {
    // Ein Wert, zwei Felder: die Auswahl plus — bei „Sonstiges“ — der Freitext.
    // Gespeichert wird wieder EIN Wert (utils/familienstand). Der Rand-Leerraum
    // des Freitextes bleibt im Formular stehen — sonst ließe sich im Feld kein
    // Leerzeichen tippen — und fällt erst in `formToApi` weg.
    const fam = familienstandZerlegen(fval("familienstand"));
    const setFamilienstand = (auswahl: string, freitext: string) =>
      setField("familienstand", familienstandZusammensetzen(auswahl, freitext));
    return (
    <Sec title="Allgemeine Informationen" icon={SEC_ICONS.info}>
      <div className="fgrid">
        <Sel label="Anrede" value={fval("anrede")} {...fon("anrede")} options={anredeOptions.map((o) => ({ value: String(o.id), label: o.salutation ?? "" }))} ph="Anrede wählen" />
        <Sel label="Titel" value={fval("titel")} {...fon("titel")} options={titelOptions.map((o) => ({ value: String(o.id), label: o.title ?? "" }))} ph="- Kein Titel -" />
        <Text label="Nachname" value={fval("lastname")} {...fon("lastname")} ph="Nachname" error={nameError} />
        <Text label="Vorname (gemäß Personalausweis)" value={fval("vorname")} onChange={onVorname} {...fpres("vorname")} ph="Vorname (gemäß Personalausweis)" />
        <Text label="Rufname" value={fval("rufname")} {...fon("rufname")} ph="Rufname" />
        {/* Dieselben vier Felder, zwei Anordnungen: Die Kontakt-Anlage stellt
            Rufname und Geburtsname nebeneinander (beide sind Namen), darunter
            Geburtsort und Geburtsdatum (beide gehören zur Geburt). Die
            Kartenansicht folgt ihr, die gewachsene Ansicht bleibt wie sie ist —
            eine ungefragt umsortierte Maske irritiert mehr, als die
            Angleichung dort nützt. */}
        {wieAnlage ? (
          <>
            <Text label="Geburtsname" value={fval("geburtsname")} {...fon("geburtsname")} ph="Geburtsname" />
            <Text label="Geburtsort" value={fval("geburtsort")} {...fon("geburtsort")} ph="Geburtsort" />
            <DateF label="Geburtsdatum" value={dateVal("birthDate")} {...fon("birthDate")} />
          </>
        ) : (
          <>
            <Text label="Geburtsort" value={fval("geburtsort")} {...fon("geburtsort")} ph="Geburtsort" />
            <DateF label="Geburtsdatum" value={dateVal("birthDate")} {...fon("birthDate")} />
            <Text label="Geburtsname" value={fval("geburtsname")} {...fon("geburtsname")} ph="Geburtsname" />
          </>
        )}
        {/* Weitere Geburtsangaben — dieselben Felder wie in der Anlage. In der
            Akte stehen sie offen: Wer die Akte öffnet, sucht etwas, und ein
            zugeklappter Block verbirgt genau dann. Leer bleiben sie einfach
            leer (die Präsenz-Hülle blendet sie in der Leseansicht aus). */}
        <Text label="Geburtszeit" value={fval("birthTime")} {...fon("birthTime")} ph="HH:MM" />
        <Text label="Geburtsgewicht (g)" value={fval("birthWeightGrams")} {...fon("birthWeightGrams")} ph="3480" />
        <Text label="Geburtsgröße (cm)" value={fval("birthHeightCm")} {...fon("birthHeightCm")} ph="52" />
        {editable ? (
          <NationalitySelect
            label="Staatsangehörigkeit"
            name="staatsang"
            value={fval("staatsang")}
            onChange={(v) => setField("staatsang", v)}
            // Detailansicht: KEIN Auto-„Deutsch“ bei leerem Feld (würde den
            // Datensatz beim Laden ungewollt „dirty“ machen).
            autoDefault={false}
          />
        ) : (
          <Text label="Staatsangehörigkeit" value={fval("staatsang")} ph="Staatsangehörigkeit" />
        )}
        {/* Geburtsland neben der Staatsangehörigkeit: Beide benennen einen
            STAAT, und nebeneinander fällt auf, wenn sie auseinanderfallen
            (eingebürgert, im Ausland geboren). Der Geburtsort weiter oben ist
            ein Ortsname und gehört nicht in dieselbe Zeile. */}
        {editable ? (
          <CountrySelect
            label="Geburtsland"
            value={fval("geburtsland")}
            onChange={(v) => setField("geburtsland", v)}
          />
        ) : (
          <Text label="Geburtsland" value={fval("geburtsland")} ph="Geburtsland" />
        )}
        {/* Der Familienstand ist eine persönliche Angabe und steht deshalb
            bei den übrigen — nicht mehr in einem eigenen Abschnitt
            „Sonstiges“, der außer ihm nur noch die Bemerkung trug. */}
        <Sel
          label="Familienstand"
          value={fam.auswahl}
          onChange={
            editable
              ? (v) => {
                  setFamilienstand(v, fam.freitext);
                  void beiFamilienstandAuswahl(v);
                }
              : undefined
          }
          {...fpres("familienstand")}
          options={FAMILIENSTAND_OPTIONS}
          ph="Familienstand auswählen"
        />
        {/* Ohne dieses Feld liefe „Sonstiges“ ins Leere: der gespeicherte
            Wert wäre der Sentinel selbst, und ein Altbestand wie „getrennt
            lebend“ ließe sich nirgends mehr ändern.
            Präsenz-Schlüssel bewusst derselbe wie beim Auswahlfeld: beide
            bearbeiten `Contact.maritalStatus`, ein eigener Schlüssel ließe
            zwei Kollegen gleichzeitig an denselben Wert. */}
        {fam.auswahl === FAMILIENSTAND_SONSTIGES && (
          <Text
            label="Familienstand (Sonstiges)"
            value={fam.freitext}
            onChange={editable ? (v) => setFamilienstand(FAMILIENSTAND_SONSTIGES, v) : undefined}
            {...fpres("familienstand")}
            ph={FAMILIENSTAND_SONSTIGES_PLATZHALTER}
            maxLength={100}
          />
        )}
        {/* Der Hochzeitstag — zwei Ereignisse, zwei Daten. Standesamt und Kirche
            fallen häufig auseinander, und gefeiert wird regelmäßig das
            kirchliche: Wer nur eines führt, gratuliert am falschen Tag. */}
        {zeigeHochzeitstag(fval("familienstand")) && (
          <>
            <DateF label="Hochzeitstag (standesamtlich)" value={dateVal("weddingDateCivil")} {...fon("weddingDateCivil")} />
            <DateF label="Hochzeitstag (kirchlich)" value={dateVal("weddingDateChurch")} {...fon("weddingDateChurch")} />
          </>
        )}
        {/* Verstorben: der Kontakt bleibt bestehen (Verträge und Vorgänge
            laufen mit den Erben weiter) — er wird nur gekennzeichnet.
            Der Schalter steht VOR dem Datum, weil er die führende Angabe ist:
            Meist erfährt man vom Tod über Dritte und kennt das Datum nicht.
            Ein erfundenes Datum wäre schlechter als keines — es sieht aus wie
            eine Tatsache und landet in Anschreiben und Fristen. */}
        <Sel label="Verstorben" value={janein("deceased")} {...onJanein("deceased")} options={JANEIN} ph="— wählen —" />
        {cd?.deceased === true && (
          <DateF label="Verstorben am" value={dateVal("deceasedAt")} {...fon("deceasedAt")} />
        )}
        {/* Die Belege zum Todesfall (T-357) — Sterbeanzeige, Sterbeurkunde,
            Kondolenzschreiben, Danksagung … als Liste. Sie erscheinen erst
            mit „Verstorben: ja", denn ohne Todesfall gibt es nichts zu
            belegen. Ein neuer Upload hängt an, er ersetzt nie.
            Sie werden NICHT mit abgeräumt, wenn jemand „verstorben" zurücknimmt:
            Beim versehentlichen Umschalten wären die Nachweise sonst weg. */}
        {cd?.deceased === true && (
          <div className="fld">
            <FeldKopf label="Belege zum Todesfall" />
            <ContactTodesfallBelege
              value={todesfallBelegeAusApi(cd?.todesfallBelege)}
              editable={editable}
              inputId="akte-todesfall-belege-datei"
              onChange={(next) => setField("todesfallBelege", next as any)}
              onFehler={meldeVollmachtFehler}
            />
          </div>
        )}
        {/* Hobbys und Vorlieben getrennt, als Chips: ein Hobby wird ausgeübt
            und ist oft versicherbar, eine Vorliebe beschreibt den Geschmack. */}
        {editable ? (
          <>
            <FeldPraesenzHuelle {...fpres("hobbys")}>
              <TagInput label="Hobbys" name="hobbys" value={fval("hobbys")} onChange={(v) => setField("hobbys", v)} placeholder="Segeln, Oldtimer, Jagd …" />
            </FeldPraesenzHuelle>
            <FeldPraesenzHuelle {...fpres("vorlieben")}>
              <TagInput label="Vorlieben" name="vorlieben" value={fval("vorlieben")} onChange={(v) => setField("vorlieben", v)} placeholder="Wein, Klassik, Fußball …" />
            </FeldPraesenzHuelle>
          </>
        ) : (
          <>
            <Text label="Hobbys" value={fval("hobbys")} ph="Hobbys" />
            <Text label="Vorlieben" value={fval("vorlieben")} ph="Vorlieben" />
          </>
        )}
        {mitBemerkung && bemerkungFeld("Freie Notiz zu diesem Kontakt")}
      </div>
    </Sec>
    );
  };

  const beruf = () => (
    <Sec title="Beruf" icon={SEC_ICONS.info}>
      <div className="fgrid">
        {/* „Angestelltenstatus", nicht „Status": Ein Kontakt hat schon einen
            Status (Kunde/Interessent/Sonstige) — zwei Felder gleichen Namens in
            derselben Akte sind eine Verwechslung, die niemand auflösen kann.
            Die Anlage heißt seit #1609 so; hier blieb es stehen. */}
        <Sel label="Angestelltenstatus" value={fval("berufsstatus")} {...fon("berufsstatus")} options={withBerufsstatusValue(berufsstatusOptions, fval("berufsstatus"))} ph="Berufsstatus wählen" />
        {/* Angestellt UND selbstständig: Der Angestelltenstatus trägt EINEN
            Wert. Wer beides ist, musste sich entscheiden — die Nebentätigkeit
            verschwand. */}
        <Sel
          label="Zusätzlich selbstständig"
          value={fval("zusaetzlichSelbststaendig")}
          {...fon("zusaetzlichSelbststaendig")}
          options={SELBSTSTAENDIGKEIT_OPTIONEN.map((o) => o.value)}
          blankLabel="Nein"
        />
        <Text label="Tätigkeit" value={fval("taetigkeit")} {...fon("taetigkeit")} ph="Tätigkeit" />
        {/* Der Arbeitgeber ist ein Firmenkontakt, keine Spalte hier: Anschrift
            und Branche zeigt das Feld AUS DER FIRMENAKTE an, statt sie zu
            kopieren. Seit 08/2026 wird er ausschließlich verknüpft — der
            Freitext ist nur noch lesbar, damit die Bestandsnamen bis zur
            Zuordnung sichtbar bleiben. */}
        <ArbeitgeberFeld
          verantwortungVonKontaktId={contactId}
          befund={arbeitgeberBefund}
          kandidaten={arbeitgeberKandidatenListe}
          freitext={fval("arbeitgeber")}
          onFirmaWaehlen={
            editable
              ? (id, name) => {
                  if (id == null) {
                    setArbeitgeberWahl(null);
                    return;
                  }
                  const firmenname =
                    name ??
                    arbeitgeberKandidatenListe.find((f) => f.companyId === id)?.companyName ??
                    "Firma";
                  setArbeitgeberWahl({ id, name: firmenname });
                  // Rolle abfragen — außer die Person ist bei dieser Firma
                  // schon erfasst; dann gibt es nichts nachzutragen.
                  const schonVerbunden = arbeitgeberKandidatenListe.some(
                    (f) => f.companyId === id,
                  );
                  if (!schonVerbunden) setRollenAbfrage({ id, name: firmenname });
                }
              : undefined
          }
          onFirmaOeffnen={(id) => navigate(`/contacts/${id}`)}
          {...fpres("arbeitgeber")}
        />
        {/* Branche der PERSON nur noch als Rückfallebene: Ist ein Arbeitgeber
            verknüpft, steht seine Branche oben — sie doppelt hier zu pflegen
            war die Entscheidung, gegen die sich der PO ausgesprochen hat. */}
        {arbeitgeberBefund.art !== "verknuepft" && (
          <Text label="Branche" value={fval("branche")} {...fon("branche")} ph="Branche" />
        )}
      </div>
      {/* Steuerklasse, Jahreseinkommen, Sozialversicherungsnummer und Ausweis.
          Bis 09/2026 gab das Backend sie nur den Kontaktverantwortlichen
          (FO/BO) sowie gfgl/admin heraus, und ein Server-Flag steuerte, ob der
          Abschnitt überhaupt erschien. Der PO hat die Sonderstellung am
          21.09.2026 aufgehoben — der Abschnitt steht jedem offen, der den
          Kontakt sehen darf. */}
        <div className="fgrid" style={{ marginTop: 18 }}>
          {/* `fonVokabular` statt `fon`: Die Leer-Option des Selects ist
              eine echte Auswahl („nicht erfasst") und muss als null
              gehen — "" verletzt den CHECK der Spalte. */}
          <Sel
            label="Steuerklasse"
            value={fval("taxClass")}
            {...fonVokabular("taxClass")}
            options={[...STEUERKLASSEN]}
            ph="Steuerklasse wählen"
          />
          {/* Dasselbe Betragsfeld wie im Kennzahlen-Reiter: es gruppiert
              WÄHREND der Eingabe und rechnet den Cursor mit; gemeldet wird
              beim Verlassen die geparste Zahl. `undefined` (geleert) geht
              als null raus — sonst verschwände der Schlüssel im JSON und
              der alte Betrag bliebe stehen. */}
          <FeldPraesenzHuelle {...fpres("annualIncome")}>
            {/* Beschriftung ohne `htmlFor`: das Betragsfeld vergibt keine
                Id — den zugänglichen Namen trägt `ariaLabel`. */}
            <label>Jahreseinkommen (€)</label>
            <GroupedNumberInput
              className={"inp" + (cd?.annualIncome == null ? " is-empty" : "")}
              ariaLabel="Jahreseinkommen in Euro"
              placeholder="84.000"
              disabled={!editable}
              value={cd?.annualIncome ?? null}
              onChange={(n) => editable && setField("annualIncome", n ?? null)}
            />
          </FeldPraesenzHuelle>
          {/* Steuer-ID und Steuernummer sind NICHT dasselbe: Die
              Identifikationsnummer (§ 139b AO) ist lebenslang und bundesweit
              eindeutig, die Steuernummer vergibt das Finanzamt und sie
              ändert sich beim Umzug. Wer selbstständig ist, hat beide. */}
          <Text
            label="Steuer-ID"
            value={fval("taxIdNumber")}
            {...fon("taxIdNumber")}
            ph="12 345 678 901"
            maxLength={16}
          />
          {/* Die Felder richten sich nach der Art (PO 23.09.2026) — wie in der
              Anlage, Vokabular in `data/ausweisarten.ts`. Wechselt die Art,
              gehören Unterart und Klassen nicht mehr dazu. */}
          <Sel
            label="Dokumentart"
            value={fval("idCardType")}
            onChange={
              editable
                ? (v) => {
                    setField("idCardType", v);
                    setField("idCardSubtype", null);
                    setField("idCardClasses", null);
                  }
                : undefined
            }
            {...fpres("idCardType")}
            options={[...AUSWEIS_ARTEN]}
          />
          {unterart?.optionen ? (
            <Sel
              label={unterart.frage}
              value={fval("idCardSubtype")}
              onChange={
                editable
                  ? (v) => {
                      setField("idCardSubtype", v || null);
                      setField("idCardClasses", null);
                    }
                  : undefined
              }
              {...fpres("idCardSubtype")}
              options={unterart.optionen}
            />
          ) : unterart ? (
            <Text
              label={unterart.frage}
              value={fval("idCardSubtype")}
              {...fon("idCardSubtype")}
              ph={unterart.platzhalter}
              maxLength={80}
            />
          ) : null}
          <Text
            label={dokFelder.nummer}
            value={fval("idCardNumber")}
            {...fon("idCardNumber")}
            maxLength={50}
          />
          {klassen ? (
            <FeldPraesenzHuelle {...fpres("idCardClasses")}>
              <MehrfachSelect
                label={klassen.label}
                name="akte-idCardClasses"
                value={klassenLesen(fval("idCardClasses"))
                  .map((k) => klassen.optionen.indexOf(k))
                  .filter((i) => i >= 0)}
                options={klassen.optionen.map((o, i) => ({ value: i, label: o }))}
                onToggle={(i) =>
                  setField(
                    "idCardClasses",
                    klasseUmschalten(fval("idCardClasses"), klassen.optionen[i], klassen.optionen) || null,
                  )
                }
                beschriftung={fval("idCardClasses") || "keine ausgewählt"}
                disabled={!editable}
              />
            </FeldPraesenzHuelle>
          ) : null}
          {/* Ausstellung und Behörde: Der Scan liest sie längst — bis 09/2026
              gab es nur kein Feld, in das sie gehen konnten. */}
          <DateF
            label="Ausgestellt am"
            value={datumFuersFeld(cd?.idCardIssuedAt)}
            onChange={editable ? (v) => setField("idCardIssuedAt", datumAusEingabe(v)) : undefined}
            {...fpres("idCardIssuedAt")}
          />
          <Text
            label={dokFelder.behoerde}
            value={fval("idCardAuthority")}
            {...fon("idCardAuthority")}
            ph={dokFelder.behoerdePlatzhalter}
            maxLength={120}
          />
          {/* Ein geleertes Datumsfeld liefert "" — für eine date-Spalte kein
              Datum (22007). Die Leerung muss als null gehen. */}
          {/* Urkunden (Geburt, Tod) laufen nicht ab — kein „Gültig bis". */}
          {!dokFelder.ohneGueltigkeit && (
            <DateF
              label={dokFelder.gueltigBis}
              value={datumFuersFeld(cd?.idCardValidUntil)}
              onChange={
                editable ? (v) => setField("idCardValidUntil", datumAusEingabe(v)) : undefined
              }
              {...fpres("idCardValidUntil")}
            />
          )}
          {/* Sozialversicherungs- und Rentenversicherungsnummer sind dieselbe
              zwölfstellige Nummer — ein Feld, nicht zwei. */}
          <Text
            label="Sozialversicherungsnummer"
            value={fval("socialSecurityNumber")}
            {...fon("socialSecurityNumber")}
            ph="65 170839 K 003"
            maxLength={32}
          />
        </div>
    </Sec>
  );

  /**
   * Alle Abschnitte des Reiters unter ihrem Schlüssel — welche davon in welcher
   * Reihenfolge erscheinen, sagt `REIHENFOLGE` (akteAbschnitte.ts).
   *
   * Der Zuschnitt einzelner Abschnitte unterscheidet sich zwischen den beiden
   * Ansichten: In der Kartenansicht stehen Social Media, Mitgliedschaften und
   * Bemerkung als eigene Karten (wie in der Kontakt-Anlage) und werden deshalb
   * aus ihren bisherigen Abschnitten herausgenommen. Die klassische Ansicht
   * ruft mit den Standardwerten auf und bleibt dadurch unverändert.
   */
  const abschnittBauen = (
    schluessel: AbschnittsSchluessel,
    karten: boolean,
  ): React.ReactNode => {
    switch (schluessel) {
      case "kontaktnummern": return kontaktnummern();
      case "allgemeinFirma": return allgemeinFirma();
      case "allgemeinFirmaKarte": return allgemeinFirmaKarte();
      case "allgemeinPerson":
        return allgemeinPerson({ mitBemerkung: !karten, wieAnlage: karten });
      case "herkunft": return herkunftSec();
      case "allgemeinProduktpartner": return allgemeinProduktpartner();
      case "allgemeinAp": return allgemeinAp();
      case "brancheTaetigkeit": return brancheTaetigkeit();
      case "steuerRecht": return steuerRecht();
      case "anschrift": return anschrift(isCompany ? "Straße" : "Straße und Hausnummer");
      case "kommunikationFirma": return kommunikationFirma({ mitSocial: !karten });
      case "kommunikationPerson": return kommunikationPerson({ mitSocial: !karten });
      case "beruf": return beruf();
      case "bank": return bank();
      case "socialSec": return socialSec();
      case "ansprache": return anspracheSec();
      case "mitgliedschaftenSec": return mitgliedschaftenSec();
      case "bemerkungSec": return bemerkungSec("Freie Notiz zu diesem Kontakt");
      case "connections": return connections();
      case "vertretungUndVollmacht": return vertretungUndVollmacht();
      case "vollmachten": return vollmachtenSec();
      case "ansprechpartnerSec": return ansprechpartnerSec();
      case "gesellschaftZuordnung": return gesellschaftZuordnung();
    }
  };

  const abschnitteRendern = (variante: AkteVariante) => {
    const karten = ansicht === "karten";
    return (
      <>
        {REIHENFOLGE[variante][karten ? "karten" : "klassisch"].map((k) => (
          <React.Fragment key={k}>{abschnittBauen(k, karten)}</React.Fragment>
        ))}
      </>
    );
  };

  // Inhalt der Nicht-„Allgemein“-Tabs: die echten, self-contained Komponenten.
  const renderTab = (): React.ReactNode => {
    if (!hasContact) {
      return <div className="empty">Dieser Bereich lädt echte Kontaktdaten — bitte einen Kontakt über die Tabelle öffnen.</div>;
    }
    switch (tab) {
      case "Kennzahlen": return <KennzahlenTab contactId={contactId} contactName={name} />;
      case "Risiken": return <ContactRisksPanel contactId={contactId} contactTyp={typ} contactName={name} onSelectTab={(v: string) => setTab(INTERNAL_TO_LABEL[v] ?? tab)} />;
      case "Verträge": return <Vertragsaufstellung kontaktId={contactId} contactName={name} />;
      case "Schäden": return <DamagesTableContact kontaktId={contactId} kontaktName={name} />;
      case "Vorgänge": return <KontaktVorgaengeReiter contactId={contactId} />;
      case "Protokolle": return <div className="empty">Die Protokolle folgen.</div>;
      case "Produktportfolio": return <PartnerPortfolio ref={portfolioRef} contactId={contactId} nurLesen={gesperrt || !editable} onKontaktOeffnen={kontaktOeffnen} onUebersicht={() => { void (async () => { if (await verlassenErlaubt()) navigate("/partnerportfolio"); })(); }} />;
      case "Termine": return <ContactTermineTab contactId={contactId} isCompany={isCompany} />;
      case "Kommunikation": return <ContactKommunikationView contactId={contactId} isCompany={isCompany} emailAddresses={emailAddresses} />;
      case "Zahlungsverkehr": return <OffenePostenTabelle contactId={contactId} />;
      case "Kundentagebuch": return <ContactKundentagebuchTab contactId={contactId} />;
      case "Dokumente": return <DocumentsSection contactId={contactId} openDocId={openDocId} searchTerm={docSearchTerm} openPfad={openOrdner} />;
      default: return <div className="empty">Dieser Bereich ist noch nicht angebunden.</div>;
    }
  };

  const renderAllgemein = (): React.ReactNode => {
    if (!hasContact) {
      return <div className="empty">Dieser Bereich lädt echte Kontaktdaten — bitte einen Kontakt über die Tabelle öffnen.</div>;
    }
    if (!cd) {
      return <div className="empty">{ladeFehler || "Kontaktdaten werden geladen …"}</div>;
    }
    // Anlage und Akte verwenden für alle Kontaktarten dasselbe Formular.
    return (
      <>
      <AkteAllgemeinAnlage
        ref={anlageRef}
        contactId={contactId}
        nurLesen={!editable}
        gesperrt={gesperrt}
        onGespeichert={reload}
        verantwortung={verantwortungVon((cd ?? {}) as unknown as Record<string, unknown>)}
        onKontaktOeffnen={kontaktOeffnen}
        onPortfolio={() => { void reiterWechseln("Produktportfolio"); }}
        // Den Kontakt hat die Akte schon — das Formular lädt ihn nicht noch einmal.
        vorgeladen={rohKontakt}
        onNeuLaden={reload}
        logoAkte={{
          vorhanden: !!photoRef,
          onGespeichert: (url, version) => {
            // Eigene Änderung, kein Konflikt fürs Formular (utils/kontaktStand.ts).
            eigenenStandMelden(contactId, version);
            setLogoLokal(url);
          },
        }}
      />
      {/* Seit „Allgemein" das Anlage-Formular ist, fehlte der Einstieg in die
          Risikoanalyse — bei Firmen steht er jetzt ganz unten rechts, wo in
          der Anlage „Analysemodus starten" sitzt (User 02.11.2026). */}
      {isCompany && !isPartnerStatus(status) && (
        <div style={{ marginTop: 16 }}>
          <AnalyseButton contactId={contactId} onRefresh={reload} rechts />
        </div>
      )}
      </>
    );
  };

  /** Speichert Kopf-Felder und (Partner-Akten) die bisherigen Abschnitte. `false` = nicht gespeichert. */
  const onSave = async (): Promise<boolean> => {
    if (!String(cd?.lastname ?? "").trim()) {
      setShowErrors(true);
      // Die Speicherleiste mit der Namensmeldung gibt es im neuen Kopf nicht
      // mehr — ohne Hinweis schlösse das Schloss scheinbar erfolgreich.
      await hinweisen({
        titel: `${isCompany ? "Firmenname" : "Nachname"} fehlt`,
        text: `Der ${isCompany ? "Firmenname" : "Nachname"} darf nicht leer sein. Bitte ergänzen und dann speichern.`,
      });
      return false;
    }
    setShowErrors(false);
    setSaving(true);
    try {
      const stammdatenGespeichert = await handleSave();
      // Eigene Ressource, aber EIN Knopf. Fehler hier dürfen die gespeicherten
      // Stammdaten nicht als „fehlgeschlagen" erscheinen lassen — deshalb eigene
      // Meldung statt eines gemeinsamen catch.
      if (branchen.dirty) {
        try {
          await branchen.speichern();
        } catch (e) {
          dispatchToast(
            <Toast>
              <ToastTitle>Branchen nicht gespeichert</ToastTitle>
              <ToastBody>{String((e as { message?: string })?.message ?? e)}</ToastBody>
            </Toast>,
            { intent: "error" },
          );
        }
      }
      // Der Arbeitgeber ist eine Beziehung, keine Kontaktspalte — eigene
      // Ressource, derselbe Knopf. NUR nach erfolgreichen Stammdaten: Dieser
      // Schritt endet mit einem Refetch, und der ersetzt `contactData`
      // vollständig. Nach einem gescheiterten PUT wären damit ausgerechnet die
      // Eingaben weg, die der Anwender gerade nicht speichern konnte — er sähe
      // „Speichern fehlgeschlagen" und ein leergeräumtes Formular.
      if (stammdatenGespeichert && arbeitgeberWahl !== undefined) {
        try {
          await arbeitgeberSetzen(contactId, arbeitgeberWahl?.id ?? null);
          // Neu laden: Erst die frisch geladenen Beziehungen tragen die
          // Markierung, aus der die Anzeige den Arbeitgeber ableitet. Wurde
          // während des Speicherns weitergetippt, bleibt der Refetch aus
          // (`reloadWennSauber`) — dann bleibt auch die Wahl stehen und geht
          // beim nächsten Speichern erneut raus, statt die frische Eingabe zu
          // verwerfen und dabei den alten Arbeitgeber zu zeigen.
          if (reloadWennSauber()) setArbeitgeberWahl(undefined);
        } catch (e) {
          dispatchToast(
            <Toast>
              <ToastTitle>Arbeitgeber nicht gespeichert</ToastTitle>
              <ToastBody>{String((e as { message?: string })?.message ?? e)}</ToastBody>
            </Toast>,
            { intent: "error" },
          );
        }
      }
      return stammdatenGespeichert;
    } finally {
      setSaving(false);
    }
  };

  // Klassifikation und Arbeitgeber zählen zum „ungespeichert"-Zustand: beide
  // haben keinen eigenen Knopf mehr, ohne sie bliebe die Speicherleiste bei
  // einer reinen Branchen- oder Arbeitgeber-Änderung weg.
  /**
   * Speichert alles, was entsperrt geändert wurde, und schließt das Schloss:
   * erst die Kopf-Felder (Jahresgespräch, persönlicher Kontakt, Organigramm;
   * bei Partner-Akten die Abschnitte), DANN das Formular. Umgekehrt lädt das
   * Formular nach seinem PUT den Kopf neu, während dessen PUT noch aussteht —
   * die Antwort konnte den alten Stand zeigen und die Kopf-Änderung wirkte
   * verloren (Befund 28.09.2026). Scheitert ein Schritt, bleibt das Schloss
   * offen, damit nichts ungespeichert „gesperrt" wird. `false` = nicht fertig.
   *
   * Derselbe Weg für das Schloss und für „Speichern" im Verlassen-Dialog.
   */
  const allesSpeichern = async (): Promise<boolean> => {
    if (editable && (isDirty || branchen.dirty || arbeitgeberWahl !== undefined)) {
      if (!(await onSave())) return false;
    }
    const ok = (await anlageRef.current?.speichern()) ?? true;
    if (!ok) return false;
    if (portfolioRef.current && !(await portfolioRef.current.speichern())) return false;
    sperren();
    return true;
  };

  /** „Verwerfen": Formular und Kopf auf den gespeicherten Stand, Schloss zu. */
  const allesVerwerfen = () => {
    anlageRef.current?.verwerfen();
    portfolioRef.current?.verwerfen();
    setArbeitgeberWahl(undefined);
    if (isDirty) reload();
    sperren();
  };

  /**
   * Gibt es gerade Ungespeichertes? Nur entsperrt — gesperrt lässt sich nichts
   * eingeben, und die Vorbelegungen der Maske direkt nach dem Laden sind kein
   * Grund zu fragen (sie zählen zum Ladestand, siehe AkteAllgemeinAnlage).
   */
  const ungespeichertRef = React.useRef<() => boolean>(() => false);
  ungespeichertRef.current = () =>
    !gesperrtRef.current &&
    ((editable && (isDirty || branchen.dirty || arbeitgeberWahl !== undefined)) ||
      !!anlageRef.current?.geaendert() || !!portfolioRef.current?.geaendert());
  const ungespeichertJetzt = React.useCallback(() => ungespeichertRef.current(), []);

  const { frage, dialog: ungespeichertDialog, setSpeichert, schliessen: frageSchliessen } = useUngespeichertFrage();
  const verlassenRef = React.useRef<() => Promise<boolean>>(async () => true);
  verlassenRef.current = async () => {
    if (!ungespeichertRef.current()) return true;
    const wahl = await frage();
    if (wahl === "abbrechen") return false;
    if (wahl === "verwerfen") {
      allesVerwerfen();
      return true;
    }
    setSpeichert(true);
    try {
      return await allesSpeichern();
    } finally {
      frageSchliessen();
    }
  };
  /**
   * Vor jedem Weg aus der Akte (Reiter, Fenster, Navigation, Umwandeln,
   * Statuswechsel mit anderer Gestalt): Ungespeichertes? Dann fragen —
   * „Speichern | Verwerfen | Abbrechen". `true` = weiter.
   */
  const verlassenErlaubt = React.useCallback(() => verlassenRef.current(), []);
  /**
   * Klick auf einen Namen in einer Beziehungsliste (User 28.09.2026): Auf der
   * Seite per Router — die Verlassen-Warnung (`useVerlassenSperre`) fragt
   * dort selbst. Im Fenster greift sie nicht; dann hier fragen und die
   * andere Akte in einem eigenen Fenster öffnen.
   */
  const kontaktOeffnen = React.useCallback(
    async (id: number) => {
      if (!imFenster) {
        navigate(`/kontakte/edit/${id}`);
        return;
      }
      if (!(await verlassenErlaubt())) return;
      const fid = `kontakt-${id}`;
      openWindow({
        id: fid,
        title: "Kontakt",
        center: true,
        initialMode: "normal",
        initialSize: { width: 1180, height: 860 },
        render: () => <ContactDetailsRedesign contactIdProp={id} imFenster fensterId={fid} />,
      });
    },
    [imFenster, navigate, verlassenErlaubt, openWindow],
  );

  // Navigation (Menü, Links, Zurück-Taste, Umwandeln, Vor/Zurück) und Tab
  // schließen/neu laden. Im Fenster lässt die Navigation die Akte stehen —
  // dort zählt das Schließen des Fensters (unten).
  useVerlassenSperre({ navigation: !imFenster, ungespeichert: ungespeichertJetzt, verlassenErlaubt });

  useEffect(() => {
    if (!imFenster || !fensterId || !setWindowCloseGuard) return;
    setWindowCloseGuard(fensterId, verlassenErlaubt);
    return () => setWindowCloseGuard(fensterId, null);
  }, [imFenster, fensterId, setWindowCloseGuard, verlassenErlaubt]);

  const reiterWechseln = async (t: string) => {
    if (t === tab) return;
    if (!(await verlassenErlaubt())) return;
    setTab(t);
  };

  // Klick in die gesperrte Maske → Hinweis an der Klickstelle, das Schloss
  // pulsiert kurz (SchlossHinweis.tsx, User 30.09.2026).
  const [schlossPuls, setSchlossPuls] = useState(false);
  const schlossPulsen = React.useCallback(() => {
    setSchlossPuls(false);
    requestAnimationFrame(() => setSchlossPuls(true));
  }, []);

  const schlossKlick = async () => {
    if (gesperrt) {
      setGesperrt(false);
      return;
    }
    setSchlossLaeuft(true);
    try {
      await allesSpeichern();
    } finally {
      setSchlossLaeuft(false);
    }
  };
  // Im neuen Kopf ist nur entsperrt etwas änderbar.
  const kopfEditierbar = editable && !gesperrt;
  const fensterOeffnen = async (minimiert: boolean) => {
    // Das Fenster ist eine NEUE Akte — Eingaben der Seite kämen nicht mit.
    if (!(await verlassenErlaubt())) return;
    const fid = `kontakt-${contactId}`;
    openWindow({
      id: fid,
      title: String(name),
      center: true,
      initialMode: "normal",
      initialSize: { width: 1180, height: 860 },
      render: () => <ContactDetailsRedesign contactIdProp={contactId} imFenster fensterId={fid} />,
    });
    if (minimiert) minimizeWindow(fid);
    navigate(-1);
  };

  const hatAenderungen =
    editable && (isDirty || branchen.dirty || arbeitgeberWahl !== undefined);
  // Schloss ohne Schreibrecht (30.09.2026): wer zuständig ist und der Stand der
  // eigenen Freigabe-Anfrage — vom Server nur ohne Schreibrecht mitgeschickt.
  // Die Antwort einer frischen Anfrage überschreibt den Stand lokal, bis neu
  // geladen wird.
  const [anfrageLokal, setAnfrageLokal] = useState<ApprovalAnfrage | null | undefined>(undefined);
  useEffect(() => setAnfrageLokal(undefined), [contactId]);
  const schreibSperreServer = (contactData as { schreibSperre?: SchreibSperre } | null)?.schreibSperre ?? null;
  const schreibSperre: SchreibSperre | null =
    schreibSperreServer || anfrageLokal !== undefined
      ? {
          foNamen: schreibSperreServer?.foNamen ?? [],
          boName: schreibSperreServer?.boName ?? null,
          anfrage: anfrageLokal !== undefined ? anfrageLokal : schreibSperreServer?.anfrage ?? null,
        }
      : null;
  const keineRechteOeffnen = () => setApproval((s) => ({ ...s, open: true, error: null }));
  // Entscheidung über die eigene Anfrage live (01.10.2026): Mitteilung bzw.
  // SSE-Wiederaufbau → Akte neu laden (canWrite + Stand der Anfrage). Bei
  // „genehmigt" lässt sich das Schloss danach sofort öffnen, bei „abgelehnt"
  // zeigt der Dialog „Deine Anfrage wurde abgelehnt (von …, …)".
  // Auch MIT Schreibrecht angemeldet: Ein Entzug nimmt es wieder — dann nur
  // neu laden, wenn nichts Ungespeichertes in der Maske steht.
  useFreigabeEntscheidungLive(hasContact ? contactId : null, true, (e) => {
    setAnfrageLokal(undefined);
    reloadWennSauber();
    if (!e) return;
    dispatchToast(
      <Toast>
        <ToastTitle>Freigabe</ToastTitle>
        <ToastBody>
          {e.art === "genehmigt"
            ? `Deine Anfrage wurde genehmigt${e.von ? ` (von ${e.von})` : ""} — du kannst den Kontakt jetzt bearbeiten.`
            : e.art === "abgelehnt"
              ? `Deine Anfrage wurde abgelehnt${e.von ? ` (von ${e.von})` : ""}.`
              : e.titel}
        </ToastBody>
      </Toast>,
      { intent: e.art === "genehmigt" ? "success" : e.art === "abgelehnt" ? "error" : "info" },
    );
  });

  // --- „Daten befüllen" (Impressum/Websuche) — nur für Firmen mit Schreibrecht ---
  const [befuellenOffen, setBefuellenOffen] = useState(false);
  // Ohne Firmennamen gibt es nichts zu suchen und nichts zu prüfen.
  const befuellenGesperrt = String(fval("lastname") ?? "").trim().length < 3;
  const zeigeBefuellen = isCompany && editable && !isPartnerStatus(status);

  /**
   * Übernimmt die im Dialog bestätigten Angaben in die Maske. Die Feldnamen
   * stammen aus der ANLAGE und werden übersetzt (AKTE_ZIELE) — der Firmenname
   * heißt hier `lastname`, Telefon/Fax/E-Mail/Website sind Kommunikationswege.
   * Geschrieben wird nur in den Formularzustand: gespeichert wird über den
   * Speichern-Knopf daneben, nicht über einen eigenen Schreibpfad.
   */
  const applyFirmendaten = (felder: Partial<BefundFelder>, weitere?: WeitereWerte) => {
    const schritte = uebernahmeSchritte(felder);
    // Neue PLZ/neuer Ort → ein alter Ortsteil passt nicht mehr
    // (ortsteilAbgleich.ts). Das Impressum liefert keinen Ortsteil.
    const neu = (feld: "plz" | "ort") =>
      schritte.find((x) => x.ziel.art === "feld" && x.ziel.feld === feld)?.wert;
    if (ortsteilLeeren({ plz: fval("plz"), ort: fval("ort"), ortsteil: fval("ortsteil") }, { plz: neu("plz"), ort: neu("ort") })) {
      setField("ortsteil", "");
    }
    for (const { ziel, wert } of schritte) {
      if (ziel.art === "feld") {
        setField(ziel.feld, wert);
        continue;
      }
      // Website wie beim Verlassen des Feldes behandeln: ohne Schema ist der
      // Wert eine relative Adresse, das ergänzte „https://" darf aber die
      // Spaltenbreite nicht reißen.
      setCommFirma(
        ziel.typ,
        ziel.typ === "homepage" ? normalizeWebUrlForStorage(wert, WEB_URL_MAX_LENGTH) : wert,
      );
    }
    // Die zusätzlichen Rufnummern und Adressen als EIGENE Zeilen — nicht über
    // `setCommFirma`, das die Standardzeile pflegt und die vorige überschriebe.
    for (const typ of ["phone", "email"] as const) {
      for (const wert of weitere?.[typ] ?? []) addCommFirma(typ, wert, "aus dem Impressum");
    }
  };

  // Bausteine des Kopfes — im neuen (Anlage-)Kopf und im bisherigen gleich.
  const avatarEl = (
          <div className="davatar">
              {photoRef ? (
                <AuthedImage className="davatar-img" src={photoRef} alt="" fallback={initials} />
              ) : (
                initials
              )}
              {/* Foto/Profilbild speichern SOFORT — deshalb wie der übrige Kopf nur
                  bei offenem Schloss (Befund 28.09.2026). */}
              {kopfEditierbar && (
                <>
                  <button type="button" className="cam" title="Foto hochladen" disabled={photoUploading} onClick={() => photoInputRef.current?.click()}>
                    {photoUploading
                      ? <svg className="cam-spin" width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><path d="M8 2.5a5.5 5.5 0 105.5 5.5" /></svg>
                      : <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="2" y="4.5" width="12" height="8.5" rx="2" /><circle cx="8" cy="8.7" r="2.2" /><path d="M5.5 4.5l1-1.5h3l1 1.5" /></svg>}
                  </button>
                  <input ref={photoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onPhotoPick} />
                  {/* Nur bei Personen: Eine Firma bekommt ihr Logo aus dem
                      Impressum oder als Favicon ihrer Website. */}
                  {!isCompany && (
                    <button
                      type="button"
                      className="cam cam-suche"
                      title="Profilbild in LinkedIn und Xing suchen"
                      onClick={() => setProfilbildOffen(true)}
                    >
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round"><circle cx="7" cy="7" r="4.3" /><path d="M10.2 10.2l3 3" /></svg>
                    </button>
                  )}
                </>
              )}
            </div>
    );
  const typEl = (
    <>
              {editable ? (
                <button
                  type="button"
                  className="dtitle-typ"
                  // Die Route trägt den GESPEICHERTEN Typ („Person"), beschriftet
                  // wird mit dem Anzeigenamen („Privat").
                  onClick={() => navigate(`/contacts/${contactId}/convert/${isCompany ? "Person" : "Firma"}`)}
                  title={`Kontakttyp ändern — zu ${isCompany ? contactTypeLabel("Person") : "Firma"} umwandeln`}
                >
                  {typ === "Person" ? PersonIcon : FirmaIcon}
                  {contactTypeLabel(typ)}
                  <span className="statuspill-caret">▾</span>
                </button>
              ) : (
                <span className="dtitle-typ static" title={contactTypeLabel(typ)}>
                  {typ === "Person" ? PersonIcon : FirmaIcon}
                  {contactTypeLabel(typ)}
                </span>
              )}
    </>
  );
  // Neuer Kopf (User 27.09.2026): Symbol vor dem Namen — verstorben geht vor
  // Geschlecht —, bei Privat mit Rufname „Rufname – Nachname, Vorname".
  const kopfSymbol = istTot
    ? { zeichen: VERSTORBEN_ZEICHEN, text: verstorbenTitel(cd as any) || "verstorben" }
    : !isCompany
      ? (() => {
          const g = geschlechtKurz((cd as any)?.geschlecht ?? (cd as any)?.gender, (cd as any)?.anrede);
          return g ? { zeichen: g === "m" ? "\u2642" : g === "w" ? "\u2640" : "\u26A5", text: g === "m" ? "männlich" : g === "w" ? "weiblich" : "divers" } : null;
        })()
      : null;
  const rufname = String((cd as any)?.rufname ?? "").trim();
  const kopfName =
    !isCompany && rufname
      ? `${rufname} – ${[String(cd?.lastname ?? "").trim(), String((cd as any)?.vorname ?? (cd as any)?.firstName ?? "").trim()].filter(Boolean).join(", ")}`
      : name;

  return (
    <div className="kvp-rd detail">
      <Toaster toasterId={toasterId} />
      {ungespeichertDialog}
      {profilbildOffen && contactId && (
        <ProfilbildDialog
          contactId={contactId}
          onClose={() => setProfilbildOffen(false)}
          onUebernommen={reload}
        />
      )}
      <div ref={kopfRef} className={"dhead nk-kopf" + (kopfZu ? " kopf-zu" : "") + (kopfKompakt ? " kopf-kompakt" : "")}>
        <div className="dhead-top">
          <div className="nk-links">{avatarEl}</div>
          <div className="dtitle nk-mitte">
            {kopfSymbol && (
              <span className="nk-symbol" title={kopfSymbol.text} aria-label={kopfSymbol.text}>
                {kopfSymbol.zeichen}
              </span>
            )}
            <h1>{kopfName}</h1>
          </div>
          <div className="dactions">
            <ContactPresenceBar betrachter={praesenz.betrachter} />
            {/* Schloss (User 28.09.2026): gesperrt = nur ansehen; beim Sperren
                wird gespeichert. */}
            {/* Ohne Schreibrecht steht das Schloss trotzdem da (30.09.2026): der
                Klick erklärt, wer zuständig ist, und bietet die Freigabe an. */}
            {!editable && cd && (
              <button
                type="button"
                className="aic aic-sm nk-schloss nk-schloss-ohne-recht"
                title="Keine Rechte — um Genehmigung bitten"
                aria-label="Keine Rechte — um Genehmigung bitten"
                onClick={keineRechteOeffnen}
              >
                {SCHLOSS_ZU}
              </button>
            )}
            {editable && (
              <button
                type="button"
                className={"aic aic-sm nk-schloss" + (gesperrt ? "" : " on") + (schlossPuls ? " puls" : "")}
                onAnimationEnd={() => setSchlossPuls(false)}
                aria-pressed={!gesperrt}
                disabled={schlossLaeuft}
                title={gesperrt ? "Entsperren — zum Bearbeiten" : "Sperren — speichert alle Änderungen"}
                aria-label={gesperrt ? "Entsperren" : "Sperren und speichern"}
                onClick={() => void schlossKlick()}
              >
                {gesperrt ? SCHLOSS_ZU : SCHLOSS_AUF}
              </button>
            )}
            <CollapseToggle
              collapsed={kopfZu}
              onToggle={toggleKopf}
              label="Kontaktkopf"
            />
            {!imFenster && (
              <>
                <button type="button" className="aic aic-sm" title="Als Fenster öffnen" aria-label="Als Fenster öffnen" onClick={() => void fensterOeffnen(false)}>
                  {FENSTER_ICON}
                </button>
                <button type="button" className="aic aic-sm" title="Minimieren — für später" aria-label="Minimieren" onClick={() => void fensterOeffnen(true)}>
                  {MINIMIEREN_ICON}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Pillen-Band zwischen Namen und Chips: Kontaktverantwortlichkeit (FO,
            BO) und Status. Bewusst eine eigene Zeile UNTER `.dhead-top` statt in
            der h1 — so beginnt sie am selben linken Anschlag wie die `.metarow`
            darunter (die Titelzeile ist um Avatar + Lücke eingerückt) und die
            drei Bänder Name / Pillen / Chips bleiben auseinanderzuhalten.
            Die Verantwortlichkeit speichert SOFORT und hängt am eigenen,
            strengeren Recht (canReassignOwnership) — nicht an `editable`. Der
            Farbschlüssel ist derselbe wie in der Kontakttabelle (FO: User-Id,
            BO: Team-Name), damit die Farbtöne dort und hier zusammenpassen. */}
        <div className="verantrow">
          {anlageAnsicht && !kopfZu && (
            <>
              <span className="nk-typ">{typEl}</span>
              <span className="nk-bewertung" title="Bewertung — folgt">☆☆☆☆☆</span>
            </>
          )}
          <VerantwortlichChip
            art="BO"
            // Wie in der Kontakttabelle: Team-Kürzel („Team " weg) in der
            // Farbe des Teams, BO vor FO (User 28.09.2026).
            wert={boName ? boName.replace(/^Team\s+/i, "") : boName}
            farbschluessel={boName || undefined}
            gewaehlt={boSelected}
            optionen={appTeams.map((tm) => ({ id: tm.id, name: tm.name }))}
            leerText="Kein BO-Team"
            leerOption="Kein BO-Team"
            editable={canReassignOwnership && !(anlageAnsicht && gesperrt)}
            onChange={(v) => void assignOwnership({ boType: v ? "TEAM" : "NONE", boTeamId: v || null })}
          />
          <FoMehrfachChip
            gewaehlt={foListeAusKontakt(cd)}
            optionen={foUsers}
            namen={users}
            editable={canReassignOwnership && !(anlageAnsicht && gesperrt)}
            onChange={(ids) => void assignOwnership({ foUserIds: ids })}
          />
          <ContactStatusPill
            status={status}
            extraStatuses={extraStatuses}
            companies={statusCompanies}
            editable={kopfEditierbar}
            // `isCompany` steht ab Zeile 902 zur Verfügung und entscheidet,
            // welche Unterkategorien das Menü anbietet.
            istFirma={isCompany}
            coIntern={hasPermission("cointern:read")}
            persoenlich={cd?.persoenlich === true}
            onChangeStatus={changePrimaryStatus}
            onJump={jumpToSection}
          />
          {anlageAnsicht && (
            <PersoenlicherKontaktPille
              userId={String((cd as any)?.inhouseContactUserId ?? "")}
              optionen={users.map((u) => ({ id: u.id, name: u.name, kuerzel: u.kuerzel }))}
              editable={kopfEditierbar}
              onChange={(id) => setField("inhouseContactUserId" as any, (id || null) as any)}
            />
          )}
        </div>

        <div className="metarow metarow-akte">
          {hasContact && (
            <KopfDokumentLeiste
              contactId={contactId}
              kontaktName={name}
              unternehmen={unternehmenDerPerson}
              zeigeErstinfo={status === "Kunde" || status === "Interessent"}
              // Altbestand der Erstinformation (Häkchen + Scan) — nur Anzeige (T-17).
              authority={cd?.authority ?? null}
              // Dokumente (Vollmacht hochladen, Protokoll, Unterschreiben lassen)
              // werden sofort abgelegt und gehören nicht zum Formular — sie
              // hängen am Schreibrecht, nicht am Schloss (Hotfix 28.09.2026).
              editable={editable}
              onFehler={meldeVollmachtFehler}
              onDokumenteOeffnen={() => void reiterWechseln("Dokumente")}
              nachOrganigramm={
                <ContactOrgchartChip
                  url={fval("orgchartUrl")}
                  name={fval("orgchartName")}
                  editable={kopfEditierbar}
                  onChange={(next) => {
                    setField("orgchartUrl", next?.url ?? null);
                    setField("orgchartName", next?.name ?? null);
                  }}
                  onFehler={meldeVollmachtFehler}
                />
              }
            />
          )}
          <span className="mchip"><span className="k">Jahresgespräch:</span>{kopfEditierbar ? (
            <input type="date" value={dateVal("letztesJahresgespraech")} onChange={(e) => setField("letztesJahresgespraech", e.target.value)} style={{ border: "none", background: "transparent", font: "inherit", color: "inherit", cursor: "pointer", padding: 0 }} />
          ) : (
            <span>{dateVal("letztesJahresgespraech") || "Nicht gesetzt"}</span>
          )}</span>
        </div>

        <div className="tabbar" ref={tabbarRef}>
          {visibleTabs(tabsIsCompany, cd ? status : undefined)
            // Zahlungsverkehr ist vorerst auf admin/gfgl beschraenkt — ohne
            // Leserecht den Reiter gar nicht anbieten statt ins Leere zu fuehren.
            .filter((t) => t !== "Zahlungsverkehr" || hasPermission("invoices:read"))
            .map((t) => (
            <button
              key={t}
              ref={tab === t ? activeTabRef : undefined}
              className={tab === t ? "on" : ""}
              onClick={() => void reiterWechseln(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* `dbody-karten` gibt dem Kartenstapel die volle Breite und den
          Abstand zwischen den Karten; der Kontext sagt jedem Abschnitt, in
          welcher Gestalt er sich zeichnen soll. Beides greift nur im Reiter
          „Allgemein“ — die übrigen Reiter kennen keine zweite Ansicht. */}
      <div className={"dbody" + (tab === "Allgemein" && ansicht === "karten" ? " dbody-karten" : "")}>
        <AkteAnsichtCtx.Provider value={tab === "Allgemein" ? ansicht : "klassisch"}>
          {tab !== "Allgemein" ? (
            renderTab()
          ) : (
            <SchlossRahmen
              gesperrt={gesperrt}
              ohneRecht={!editable}
              onSchlossHinweis={schlossPulsen}
              onGesperrtOhneRecht={keineRechteOeffnen}
            >
              {renderAllgemein()}
            </SchlossRahmen>
          )}
        </AkteAnsichtCtx.Provider>
      </div>

      {/* Impressums-/Websuche für Firmen — dieselbe Auswahl wie in der Anlage,
          nur entscheidet hier der Anwender je Feld, ob ein bereits gepflegter
          Wert ersetzt wird. */}
      {zeigeBefuellen && (
        <FirmendatenBefuellenDialog
          open={befuellenOffen}
          eingabe={{
            name: String(fval("lastname") ?? "").trim(),
            ort: fval("ort") || null,
            plz: fval("plz") || null,
            strasse: fval("strasse") || null,
            website: commValueFirma("homepage") || null,
            email: commValueFirma("email") || null,
          }}
          vorhanden={vorhandeneAkteWerte(fval, commValueFirma)}
          // In einer gepflegten Akte ist fast jedes Feld belegt — die Auswahl
          // bliebe sonst leer. Vorausgewählt sind trotzdem nur leere Felder.
          darfErsetzen={() => true}
          onClose={() => setBefuellenOffen(false)}
          onUebernehmen={applyFirmendaten}
        />
      )}

      {/* Schloss ohne Schreibrecht: wer zuständig ist, Stand der eigenen
          Anfrage, Begründung und „Um Genehmigung bitten" (30.09.2026). */}
      {!editable && (
        <KeineRechteDialog
          open={approval.open}
          contactName={name}
          schreibSperre={schreibSperre}
          busy={approval.busy}
          error={approval.error}
          onCancel={() => setApproval((s) => ({ ...s, open: false, error: null }))}
          onSubmit={(begruendung) => {
            setApproval((s) => ({ ...s, busy: true, error: null }));
            void (async () => {
              try {
                const { data } = await apiFetch<ApprovalRequestResponse>(
                  `contact/${contactId}/approval-request`,
                  {
                    method: "POST",
                    // Ohne Content-Type schickt der Browser text/plain; Nest parst
                    // den Body dann nicht und @Body() bleibt leer — bei HTTP 200.
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ reason: begruendung || undefined }),
                  },
                );
                if (data.anfrage !== undefined) setAnfrageLokal(data.anfrage ?? null);
                setApproval({ open: !approvalGesendet(data), busy: false, error: null });
                dispatchToast(
                  <Toast>
                    <ToastTitle>Freigabe</ToastTitle>
                    <ToastBody>{approvalMeldung(data)}</ToastBody>
                  </Toast>,
                  { intent: data.status === "sent" || data.status === "pending" ? "success" : "info" },
                );
              } catch {
                setApproval((s) => ({
                  ...s,
                  busy: false,
                  error: "Die Anfrage konnte nicht gesendet werden. Bitte später erneut versuchen.",
                }));
              }
            })();
          }}
        />
      )}

      {/* T3: „Ehepartner/Lebenspartner/Ex-Partner erfassen" — Beziehung mit vorbelegtem Label. */}
      {partnerVorlage && cd?.kontaktId && (
        <RelationCreateDialog
          contactId={cd.kontaktId}
          kind={partnerVorlage.imHaushalt ? "household" : "weitere"}
          selfName={name}
          selfGender={geschlechtAktuell}
          selfSalutation={anredeLabelAktuell}
          initialLabel={partnerVorlage.label}
          onClose={() => setPartnerVorlage(null)}
          onSaved={() => reloadWennSauber()}
        />
      )}

      {/* Rollenabfrage nach dem Verknüpfen eines Arbeitgebers. Der Dialog
          verlangt mindestens eine Verantwortlichkeit oder „Mensch im
          Unternehmen" — Wegklicken ohne Angabe ist nicht vorgesehen, deshalb
          gibt es hier kein Abbrechen ohne Folgen: Wird er geschlossen, bleibt
          die Firma zwar gewählt, die Verbindung fehlt aber und der Hinweis
          erscheint beim nächsten Öffnen der Akte erneut. */}
      {rollenAbfrage && cd?.kontaktId && (
        <MenschenFuersUnternehmenDialog
          perspective="person"
          mode="add"
          kontext="firma"
          companyId={rollenAbfrage.id}
          fixedMensch={{
            contactId: cd.kontaktId,
            type: "Person",
            displayName: name,
          }}
          festeFirma={{
            contactId: rollenAbfrage.id,
            type: "Firma",
            displayName: rollenAbfrage.name,
          }}
          onClose={() => setRollenAbfrage(null)}
          onSaved={() => {
            setRollenAbfrage(null);
            reloadWennSauber();
          }}
        />
      )}

      <DataModal
        open={dm.open}
        tab={dm.tab}
        contactName={name}
        dmData={dmData}
        contactId={contactId}
        canWrite={editable}
        reload={reload}
        onTab={(t) => setDm((s) => ({ ...s, tab: t }))}
        onClose={() => setDm((s) => ({ ...s, open: false }))}
        istFirma={isCompany}
        firmen={waehlbareFirmen}
        kontoinhaber={kontoinhaberAkte}
      />
    </div>
  );
};

export default ContactDetailsRedesign;
